<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <div class="modal-title" style="display: flex; flex-direction: row; align-items: flex-start; column-gap: 10px;">
          <img src="../assets/img/wsp-logo.png" style="padding-top: 5px; width: 100px;" alt="WSP Logo" class="header-logo" />
          <img src="../assets/img/ping-mct-logo.svg" style="width: 100px;" alt="Ping MCT Logo" class="header-logo" />
        </div>
        <button class="close-button" @click="closeModal" aria-label="Close">
          <span>&times;</span>
        </button>
      </div>

      <div class="modal-body">
        <!-- Custom Step Wizard -->
        <div class="step-wizard">
          <!-- Step Navigation -->
          <div class="step-nav" :data-progress="currentStep">
            <div
              v-for="(step, index) in wizardSteps"
              :key="index"
              class="step-nav-item"
              :class="{
                active: currentStep === index,
                completed: currentStep > index,
                disabled: currentStep < index,
              }"
              @click="goToStep(index)"
            >
              <div class="step-circle">
                <i v-if="currentStep > index" class="fa fa-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-title">{{ step.title }}</span>
            </div>
          </div>

          <!-- Step Content -->
          <div class="step-content-container">
            <!-- Step 0: Compose Message -->
            <ComposeMessageStep
              v-if="currentStep === 0"
              :message-subject="messageSubject"
              :message-content="messageContent"
              :message-type="messageType"
              :response-type="responseType"
              :selected-message-type="selectedMessageType"
              :selected-crisis-type="selectedCrisisType"
              :selected-event="selectedEvent"
              :message-types="messageTypes"
              :events="events"
              :fetching-message-types="fetchingMessageTypes"
              :fetching-events="fetchingEvents"
              :saved-message-id="savedMessageId"
              :selected-sites-count="getSelectedSitesCount()"
              :message-templates="messageTemplates"
              :mct-server-route="mctServerRoute"
              :templates="templates"
              :loading-templates="loadingTemplates"
              :message-contents="messageContents"
              :scheduling-rules="schedulingRules"
              :fetching-scheduling-rules="fetchingSchedulingRules"
              :max-body-length="maxBodyLength"
              @update:messageSubject="messageSubject = $event"
              @update:messageContent="messageContent = $event"
              @update:messageContents="messageContents = $event"
              @update:messageType="messageType = $event"
              @update:responseType="responseType = $event"
              @update:selectedMessageType="selectedMessageType = $event"
              @update:selectedEvent="selectedEvent = $event"
              @refresh-events="fetchEvents"
              @fetch-templates="fetchTemplates"
            />

            <!-- Step 1: Locations & Contacts (MERGED) -->
            <LocationsWithContactsStep
              v-if="currentStep === 1"
              :sites="selectedSites"
              :selected-site-keys="selectedSiteKeys"
              :selected-sites-count="getSelectedSitesCount()"
              :paginated-sites="paginatedSortedSites"
              :is-all-selected="isAllSelected"
              :is-indeterminate="isIndeterminate"
              :current-page="currentPage"
              :total-pages="totalPages"
              :contacts-by-site="contactsBySite"
              :total-contacts-by-site="totalContactsBySite"
              :deleted-contacts="deletedContacts"
              :deleted-contacts-by-site="deletedContactsBySite"
              :showing-deleted-contacts="showingDeleted"
              :contacts-filter="contactsSearchFilter"
              :message-type-code="selectedMessageType"
              :message-type-name="getMessageTypeName(selectedMessageType)"
              :priority="messageType"
              :response-option="responseType"
              @toggle-all="toggleAllSites"
              @toggle-site="toggleSiteSelection"
              @go-to-page="goToPage"
              @go-to-previous-page="goToPreviousPage"
              @go-to-next-page="goToNextPage"
              @update-contacts-filter="contactsSearchFilter = $event"
              @remove-contact="removeContact"
              @restore-contact="restoreContact"
              @show-deleted="showDeletedContacts"
              @hide-deleted="hideDeletedContacts"
            />

            <!-- Step 2: CC Recipients -->
            <div v-if="currentStep === 2" class="step-content-wrapper">
              <CCRecipientsStep
                :cc-emails="ccEmails"
                :new-cc-email="newCcEmail"
                @update:newCcEmail="newCcEmail = $event"
                @add-cc-email="addCcEmail"
                @remove-cc-email="removeCcEmail"
              />
            </div>
            
            <!-- OLD Step 1: Impacted Locations (DEPRECATED - keeping for reference) -->
            <div v-if="false && currentStep === 1" class="step-content">
              <h3>Impacted Locations</h3>
              <span class="selection-count">
                {{ getSelectedSitesCount() }} of
                {{ selectedSites.length }} sites selected
              </span>
              <!-- </div> -->
              <div
                class="sites-table-container"
                v-if="selectedSites && selectedSites.length > 0"
              >
                <!-- Table Controls -->
                <div class="table-controls">
                  <button
                    class="btn btn-secondary btn-sm toggle-group-btn"
                    @click="toggleGrouping"
                  >
                    <i
                      :class="isGrouped ? 'fa fa-list' : 'fa fa-layer-group'"
                    ></i>
                    {{ isGrouped ? "Show as List" : "Group by Region" }}
                  </button>
                </div>
                <div class="sites-table-wrapper">
                  <table class="sites-table">
                    <thead>
                      <tr>
                        <th class="checkbox-column">
                          <input
                            type="checkbox"
                            :checked="isAllSelected"
                            :indeterminate="isIndeterminate"
                            @change="toggleAllSites"
                            ref="selectAllCheckbox"
                          />
                        </th>
                        <th class="name-column">Site Name</th>
                        <th class="office-leader-column">Office Leader</th>
                        <th class="office-address-column">Office Address</th>
                        <th class="risk-column">Risk Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Grouped View -->
                      <template v-if="isGrouped">
                        <template
                          v-for="(group, regionId) in paginatedGroupedSites"
                        >
                          <!-- Region Header Row -->
                          <tr class="region-header-row">
                            <td class="region-checkbox-cell">
                              <input
                                type="checkbox"
                                :checked="isRegionSelected(regionId)"
                                :indeterminate="isRegionIndeterminate(regionId)"
                                @change="toggleRegionSelection(regionId)"
                              />
                            </td>
                            <td colspan="4" class="region-header-cell">
                              <div class="region-header">
                                <i class="fa fa-building"></i>
                                <span class="region-name">{{ regionId }}</span>
                                <span class="region-count"
                                  >({{ group.length }} sites)</span
                                >
                              </div>
                            </td>
                          </tr>

                          <!-- Site Rows for this Region -->
                          <tr
                            v-for="ss in group"
                            :key="ss.key"
                            class="site-row"
                            :class="{
                              selected: selectedSiteKeys.includes(ss.key),
                              disabled: !isSiteSelectable(ss),
                            }"
                            @click="
                              isSiteSelectable(ss)
                                ? toggleSiteSelection(ss.key)
                                : null
                            "
                          >
                            <td class="checkbox-cell">
                              <input
                                type="checkbox"
                                :checked="selectedSiteKeys.includes(ss.key)"
                                :disabled="!isSiteSelectable(ss)"
                                @click.stop="
                                  isSiteSelectable(ss)
                                    ? toggleSiteSelection(ss.key)
                                    : null
                                "
                              />
                            </td>
                            <td class="name-cell">
                              <div class="site-name">
                                {{ ss.site.name || ss.site.id }}
                              </div>
                            </td>
                            <td class="office-leader-cell">
                              <span class="office-leader">{{
                                getOfficeLeader(ss)
                              }}</span>
                            </td>
                            <td class="office-address-cell">
                              <span class="office-address">{{
                                getSiteAddress(ss)
                              }}</span>
                            </td>
                            <td class="risk-cell">
                              <span
                                class="site-risk"
                                :class="getRiskClass(ss.site.maxRiskLevel)"
                              >
                                {{ ss.site.maxRiskLevel || 0 }}
                              </span>
                            </td>
                          </tr>
                        </template>
                      </template>

                      <!-- Non-Grouped View -->
                      <template v-else>
                        <tr
                          v-for="ss in paginatedSortedSites"
                          :key="ss.key"
                          class="site-row simple-row"
                          :class="{
                            selected: selectedSiteKeys.includes(ss.key),
                            disabled: !isSiteSelectable(ss),
                          }"
                          @click="
                            isSiteSelectable(ss)
                              ? toggleSiteSelection(ss.key)
                              : null
                          "
                        >
                          <td class="checkbox-cell">
                            <input
                              type="checkbox"
                              :checked="selectedSiteKeys.includes(ss.key)"
                              :disabled="!isSiteSelectable(ss)"
                              @click.stop="
                                isSiteSelectable(ss)
                                  ? toggleSiteSelection(ss.key)
                                  : null
                              "
                            />
                          </td>
                          <td class="name-cell">
                            <div class="site-name">
                              {{ ss.site.name || ss.site.id }}
                            </div>
                          </td>
                          <td class="office-leader-cell">
                            <span class="office-leader">{{
                              getOfficeLeader(ss)
                            }}</span>
                          </td>
                          <td class="office-address-cell">
                            <span class="office-address">{{
                              getSiteAddress(ss)
                            }}</span>
                          </td>
                          <td class="risk-cell">
                            <span
                              class="site-risk"
                              :class="getRiskClass(ss.site.maxRiskLevel)"
                            >
                              {{ ss.site.maxRiskLevel || 0 }}
                            </span>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination Controls -->
                <div class="pagination-container" v-if="totalPages > 1">
                  <div class="pagination-info">
                    <span>Page {{ currentPage }} of {{ totalPages }}</span>
                    <span class="items-info">
                      ({{
                        isGrouped
                          ? Object.keys(groupedSites).length
                          : sortedSites.length
                      }}
                      {{ isGrouped ? "regions" : "sites" }} total)
                    </span>
                  </div>

                  <div class="pagination-controls">
                    <button
                      class="btn btn-sm btn-outline-secondary pagination-btn"
                      @click="goToPreviousPage"
                      :disabled="currentPage <= 1"
                    >
                      <i class="fa fa-chevron-left"></i>
                      Previous
                    </button>

                    <div class="page-numbers">
                      <button
                        v-for="page in totalPages"
                        :key="page"
                        class="btn btn-sm page-number-btn"
                        :class="{
                          'btn-primary': page === currentPage,
                          'btn-outline-secondary': page !== currentPage,
                        }"
                        @click="goToPage(page)"
                        v-if="
                          Math.abs(page - currentPage) <= 2 ||
                            page === 1 ||
                            page === totalPages
                        "
                      >
                        {{ page }}
                      </button>

                      <span
                        v-if="totalPages > 7 && currentPage < totalPages - 3"
                        class="pagination-ellipsis"
                        >...</span
                      >
                    </div>

                    <button
                      class="btn btn-sm btn-outline-secondary pagination-btn"
                      @click="goToNextPage"
                      :disabled="currentPage >= totalPages"
                    >
                      Next
                      <i class="fa fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="no-sites-message">
                <i class="fa fa-info-circle"></i>
                <p>No sites available for selection.</p>
              </div>
            </div>

            <!-- OLD Step 0: Compose Message (DEPRECATED - now using ComposeMessageStep component) -->
            <div v-if="false && currentStep === 0" class="step-content">
              <div class="compose-header">
                <h3>Compose Message</h3>
                <div class="template-section">
                  <button
                    class="btn btn-outline-secondary btn-sm templates-btn"
                    @click="showTemplateDropdown = !showTemplateDropdown"
                  >
                    <i class="fa fa-file-text"></i>
                    Templates
                    <i
                      :class="
                        showTemplateDropdown
                          ? 'fa fa-chevron-up'
                          : 'fa fa-chevron-down'
                      "
                    ></i>
                  </button>

                  <!-- Template Dropdown -->
                  <div v-if="showTemplateDropdown" class="template-dropdown">
                    <div class="template-dropdown-header">
                      <span>Select a template to auto-fill the message</span>
                    </div>
                    <div class="template-list">
                      <div
                        v-for="template in messageTemplates"
                        :key="template.id"
                        class="template-item"
                        @click="applyTemplate(template)"
                      >
                        <div class="template-name">{{ template.name }}</div>
                        <div class="template-preview">
                          {{ template.subject }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Message Composer -->
              <div class="message-composer">
                <!-- Message Saved Indicator -->
                <div v-if="savedMessageId" class="alert alert-success message-saved-alert">
                  <i class="fa fa-check-circle"></i>
                  <strong>Message Saved!</strong> Message ID: {{ savedMessageId }}
                  <small class="d-block mt-1">Fields are now read-only. To make changes, cancel and start over.</small>
                </div>

                <div class="form-group">
                  <label for="messageSubject"
                    >Subject <span class="required">*</span></label
                  >
                  <input
                    id="messageSubject"
                    type="text"
                    v-model="messageSubject"
                    class="form-control"
                    placeholder="Enter email subject..."
                    maxlength="200"
                    :readonly="!!savedMessageId"
                    :disabled="!!savedMessageId"
                  />
                  <div class="field-hint">
                    {{ messageSubject.length }}/200 characters
                  </div>
                </div>

                <div class="form-group">
                  <label for="messageTypeSelect"
                    >Message Type <span class="required">*</span></label
                  >
                  <select
                    id="messageTypeSelect"
                    v-model="selectedMessageType"
                    class="form-control"
                    :disabled="!!savedMessageId"
                  >
                    <option value="">Select a message type...</option>
                    <option
                      v-for="msgType in messageTypes"
                      :key="msgType.id"
                      :value="msgType.code"
                      :disabled="!msgType.isActive"
                    >
                      {{ msgType.name }}{{ msgType.isActive ? '' : ' (Inactive)' }}
                    </option>
                  </select>
                  <div class="field-hint" v-if="messageTypes.length === 0 && !fetchingMessageTypes">
                    No message types available
                  </div>
                  <div class="field-hint" v-if="fetchingMessageTypes">
                    <i class="fa fa-spinner fa-spin"></i> Loading message types...
                  </div>
                </div>

                <div class="form-group">
                  <label for="messageType">Priority Level</label>
                  <select
                    id="messageType"
                    v-model="messageType"
                    class="form-control"
                    :disabled="!!savedMessageId"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="responseType">Response Option</label>
                  <select
                    id="responseType"
                    v-model="responseType"
                    class="form-control"
                    :disabled="!!savedMessageId"
                  >
                    <option value="none">None</option>
                    <option value="ok_not_ok">OK/Not OK</option>
                  </select>
                  <div class="field-hint" v-if="responseType === 'ok_not_ok'">
                    Recipients will be able to respond with OK or Not OK
                  </div>
                </div>

                <div class="form-group">
                  <label for="crisisType"
                    >Crisis Type <span class="required">*</span></label
                  >
                  <select
                    id="crisisType"
                    v-model="selectedCrisisType"
                    class="form-control"
                    :disabled="!!savedMessageId || fetchingCrisisTypes"
                  >
                    <option value="">Select a crisis type...</option>
                    <option
                      v-for="crisis in crisisTypes"
                      :key="crisis.id"
                      :value="crisis.id"
                    >
                      {{ crisis.name }}
                    </option>
                  </select>
                  <div class="field-hint" v-if="crisisTypes.length === 0 && !fetchingCrisisTypes">
                    No crisis types available
                  </div>
                  <div class="field-hint" v-if="fetchingCrisisTypes">
                    <i class="fa fa-spinner fa-spin"></i> Loading crisis types...
                  </div>
                </div>

                <div class="form-group">
                  <label for="messageBody"
                    >Message Body <span class="required">*</span></label
                  >

                  <!-- Rich Text Editor Toolbar -->
                  <div class="rich-editor-toolbar" v-if="!savedMessageId">
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('bold')"
                      :class="{ active: isFormatActive('bold') }"
                      title="Bold"
                    >
                      <i class="fa fa-bold"></i>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('italic')"
                      :class="{ active: isFormatActive('italic') }"
                      title="Italic"
                    >
                      <i class="fa fa-italic"></i>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('underline')"
                      :class="{ active: isFormatActive('underline') }"
                      title="Underline"
                    >
                      <i class="fa fa-underline"></i>
                    </button>

                    <div class="toolbar-separator"></div>

                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('insertUnorderedList')"
                      title="Bullet List"
                    >
                      <i class="fa fa-list-ul"></i>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('insertOrderedList')"
                      title="Numbered List"
                    >
                      <i class="fa fa-list-ol"></i>
                    </button>

                    <div class="toolbar-separator"></div>

                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('justifyLeft')"
                      title="Align Left"
                    >
                      <i class="fa fa-align-left"></i>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('justifyCenter')"
                      title="Align Center"
                    >
                      <i class="fa fa-align-center"></i>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      @click="formatText('justifyRight')"
                      title="Align Right"
                    >
                      <i class="fa fa-align-right"></i>
                    </button>
                  </div>

                  <!-- Rich Text Editor -->
                  <div
                    ref="richEditor"
                    class="rich-text-editor form-control"
                    :contenteditable="!savedMessageId"
                    @input="updateMessageContent"
                    @paste="handlePaste"
                    @keydown="handleKeydown"
                    placeholder="Enter your message content..."
                    :class="{ 'disabled-editor': savedMessageId }"
                  ></div>

                  <div class="field-hint">
                    {{ getPlainTextLength() }}/2000 characters
                  </div>
                </div>

                <div class="form-group">
                  <label for="messageAttachment">Attachments</label>
                  <div class="attachment-field">
                    <input
                      id="messageAttachment"
                      type="file"
                      class="form-control file-input"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      disabled
                    />
                    <div class="attachment-placeholder">
                      <i class="fa fa-paperclip"></i>
                      <span>Select files to attach</span>
                    </div>
                  </div>
                  <div class="field-hint future-feature">
                    <i class="fa fa-info-circle"></i>
                    Note: File attachments are a future feature and not yet
                    implemented
                  </div>
                </div>
              </div>
            </div>

            <!-- OLD Step 2: Recipients (DEPRECATED - merged into step 1) -->
            <div v-if="false && currentStep === 2" class="step-content">
              <h3>Select Recipients</h3>
              <p class="step-description">
                Review and select the recipients who will receive this message.
              </p>

              <!-- Recipients Table -->
              <div class="recipients-container">
                <div class="recipients-summary">
                  <span class="recipients-count">
                    {{ filteredContacts.length }} of {{ contacts.length }} recipients from {{ getSelectedSitesCount() }} selected sites
                    <span v-if="deletedContacts.length > 0" class="deleted-count">
                      ({{ deletedContacts.length }} deleted)
                    </span>
                  </span>
                  <div class="summary-actions">
                    <button
                      v-if="deletedContacts.length > 0"
                      class="btn btn-sm btn-outline-info restore-deleted-btn"
                      @click="showDeletedContacts"
                      type="button"
                    >
                      <i class="fa fa-undo"></i> View Deleted ({{ deletedContacts.length }})
                    </button>
                    <button
                      v-if="hasActiveFilters"
                      class="btn btn-sm btn-outline-secondary clear-filters-btn"
                      @click="clearContactFilters"
                      type="button"
                    >
                      <i class="fa fa-times"></i> Clear Filters
                    </button>
                  </div>
                </div>

                <div class="recipients-table-wrapper">
                  <table class="recipients-table">
                    <thead>
                      <tr>
                        <th class="name-column">Name</th>
                        <th class="email-column">Email</th>
                        <th class="phone-column">Cell</th>
                        <th class="language-column">Language</th>
                        <th class="role-column">Role(s)</th>
                        <th class="contact-type-column">Contact Type</th>
                        <th class="response-team-column">Response Team</th>
                        <th class="actions-column">Actions</th>
                      </tr>
                      <tr class="filter-row">
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.name"
                            placeholder="Filter name..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.email"
                            placeholder="Filter email..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.cell"
                            placeholder="Filter cell..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.language"
                            placeholder="Lang..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.role"
                            placeholder="Filter role..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            v-model="contactFilters.contactType"
                            placeholder="Filter type..."
                            class="filter-input"
                          />
                        </th>
                        <th>
                          <select
                            v-model="contactFilters.responseTeam"
                            class="filter-select"
                          >
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-if="fetchingContacts"
                        class="loading-row"
                      >
                        <td colspan="8" class="text-center">
                          <i class="fa fa-spinner fa-spin"></i> Loading contacts...
                        </td>
                      </tr>
                      <tr
                        v-else-if="filteredContacts.length === 0 && contacts.length === 0"
                        class="empty-row"
                      >
                        <td colspan="8" class="text-center">
                          No contacts found for selected locations
                        </td>
                      </tr>
                      <tr
                        v-else-if="filteredContacts.length === 0 && contacts.length > 0"
                        class="empty-row"
                      >
                        <td colspan="8" class="text-center">
                          No contacts match the current filters
                        </td>
                      </tr>
                      <tr
                        v-else
                        v-for="contact in paginatedContacts"
                        :key="`${contact.id}-${getContactLocationId(contact)}`"
                        class="recipient-row"
                      >
                        <td class="name-cell">
                          {{ contact.name || 'N/A' }}
                        </td>
                        <td class="email-cell">
                          {{ contact.email || 'N/A' }}
                        </td>
                        <td class="phone-cell">
                          {{ contact.cell || 'N/A' }}
                        </td>
                        <td class="language-cell">
                          {{ contact.preferredLanguageCode || 'N/A' }}
                        </td>
                        <td class="role-cell">
                          <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                            {{ getContactRoles(contact) }}
                          </span>
                          <span v-else>N/A</span>
                        </td>
                        <td class="contact-type-cell">
                          <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                            {{ getContactTypes(contact) }}
                          </span>
                          <span v-else>N/A</span>
                        </td>
                        <td class="response-team-cell">
                          <span :class="['badge', contact.isResponseTeam ? 'badge-success' : 'badge-secondary']">
                            {{ contact.isResponseTeam ? 'Yes' : 'No' }}
                          </span>
                        </td>
                        <td class="actions-cell">
                          <button
                            type="button"
                            class="btn-remove-contact"
                            @click="removeContact(contact.id, getContactLocationId(contact))"
                            title="Remove contact from list"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Contacts Pagination -->
                <div v-if="filteredContacts.length > contactsPerPage" class="contacts-pagination">
                  <div class="pagination-info">
                    Showing {{ ((contactsCurrentPage - 1) * contactsPerPage) + 1 }} to {{ Math.min(contactsCurrentPage * contactsPerPage, filteredContacts.length) }} of {{ filteredContacts.length }} contacts
                  </div>
                  <div class="pagination-controls">
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="contactsCurrentPage = 1"
                      :disabled="contactsCurrentPage === 1"
                      type="button"
                    >
                      <i class="fa fa-angle-double-left"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="contactsCurrentPage--"
                      :disabled="contactsCurrentPage === 1"
                      type="button"
                    >
                      <i class="fa fa-angle-left"></i>
                    </button>
                    <span class="pagination-current">
                      Page {{ contactsCurrentPage }} of {{ contactsTotalPages }}
                    </span>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="contactsCurrentPage++"
                      :disabled="contactsCurrentPage === contactsTotalPages"
                      type="button"
                    >
                      <i class="fa fa-angle-right"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="contactsCurrentPage = contactsTotalPages"
                      :disabled="contactsCurrentPage === contactsTotalPages"
                      type="button"
                    >
                      <i class="fa fa-angle-double-right"></i>
                    </button>
                  </div>
                </div>

                <!-- Deleted Contacts Section -->
                <div v-if="showingDeletedContacts && deletedContacts.length > 0" class="deleted-contacts-section">
                  <div class="deleted-contacts-header">
                    <h4>Deleted Contacts ({{ deletedContacts.length }})</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="hideDeletedContacts"
                    >
                      <i class="fa fa-times"></i> Close
                    </button>
                  </div>
                  <div class="deleted-contacts-table-wrapper">
                    <table class="recipients-table">
                      <thead>
                        <tr>
                          <th class="name-column">Name</th>
                          <th class="email-column">Email</th>
                          <th class="phone-column">Cell</th>
                          <th class="language-column">Language</th>
                          <th class="role-column">Role(s)</th>
                          <th class="contact-type-column">Contact Type</th>
                          <th class="response-team-column">Response Team</th>
                          <th class="actions-column">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="contact in deletedContacts"
                          :key="`deleted-${contact.id}-${getContactLocationId(contact)}`"
                          class="deleted-row"
                        >
                          <td class="name-cell">
                            {{ contact.name || 'N/A' }}
                          </td>
                          <td class="email-cell">
                            {{ contact.email || 'N/A' }}
                          </td>
                          <td class="phone-cell">
                            {{ contact.cell || 'N/A' }}
                          </td>
                          <td class="language-cell">
                            {{ contact.preferredLanguageCode || 'N/A' }}
                          </td>
                          <td class="role-cell">
                            <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                              {{ getContactRoles(contact) }}
                            </span>
                            <span v-else>N/A</span>
                          </td>
                          <td class="contact-type-cell">
                            <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                              {{ getContactTypes(contact) }}
                            </span>
                            <span v-else>N/A</span>
                          </td>
                          <td class="response-team-cell">
                            <span :class="['badge', contact.isResponseTeam ? 'badge-success' : 'badge-secondary']">
                              {{ contact.isResponseTeam ? 'Yes' : 'No' }}
                            </span>
                          </td>
                          <td class="actions-cell">
                            <button
                              type="button"
                              class="btn-restore-contact"
                              @click="restoreContact(contact.id, getContactLocationId(contact))"
                              title="Restore contact to list"
                            >
                              <i class="fa fa-undo"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- CC Section -->
                <div class="cc-section">
                  <div class="cc-header">
                    <h4><i class="fa fa-envelope"></i> CC (Additional Recipients)</h4>
                    <span class="cc-count">{{ ccEmails.length }} CC recipient(s)</span>
                  </div>
                  <div class="cc-input-group">
                    <input
                      type="email"
                      v-model="newCcEmail"
                      placeholder="Enter email address (e.g., user@example.com)"
                      class="cc-input"
                      @keyup.enter="addCcEmail"
                    />
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="addCcEmail"
                    >
                      <i class="fa fa-plus"></i> Add
                    </button>
                  </div>
                  <div v-if="ccEmails.length > 0" class="cc-list">
                    <div
                      v-for="(email, index) in ccEmails"
                      :key="index"
                      class="cc-item"
                    >
                      <span class="cc-email">{{ email }}</span>
                      <button
                        type="button"
                        class="btn-remove-cc"
                        @click="removeCcEmail(index)"
                        title="Remove email"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <div v-else class="cc-empty">
                    No additional CC recipients added
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Preview & Send -->
            <PreviewSendStep
              v-if="currentStep === 3"
              :message-subject="messageSubject"
              :message-content="messageContent"
              :message-type="messageType"
              :response-type="responseType"
              :selected-send-option="selectedSendOption"
              :message-type-name="getMessageTypeName(selectedMessageType)"
              :crisis-type-name="selectedEventCrisisTypeName"
              :event-name="getEventName(selectedEvent)"
              :contacts="activeContacts"
              :cc-emails="ccEmails"
              :message-contents="messageContents"
              :selected-message-type="selectedMessageType"
              @update:selectedSendOption="selectedSendOption = $event"
            />

            <!-- OLD Step 3: Preview/Send (DEPRECATED - now using PreviewSendStep component) -->
            <div v-if="false && currentStep === 3" class="step-content">
              <!-- Message Preview -->
              <div class="message-preview-final">
                <h4>Message Preview</h4>
                <div class="preview-card">
                  <div class="preview-header">
                    <div class="preview-subject">
                      {{ messageSubject || "No subject" }}
                    </div>
                    <div
                      class="preview-priority"
                      :class="'priority-' + messageType"
                    >
                      <i :class="getPriorityIcon(messageType)"></i>
                      {{
                        messageType.charAt(0).toUpperCase() +
                          messageType.slice(1)
                      }}
                    </div>
                  </div>
                  <div class="preview-body">
                    <div v-if="messageContent" v-html="messageContent"></div>
                    <div v-else class="no-content">No message content</div>
                  </div>

                  <!-- Response Buttons (shown only when response type is ok/not ok) -->
                  <div
                    v-if="responseType === 'ok_not_ok'"
                    class="preview-response-buttons"
                  >
                    <div class="response-buttons-header">
                      <small>Recipients will see these response options:</small>
                    </div>
                    <div class="response-buttons">
                      <button class="response-btn response-btn-ok" disabled>
                        <i class="fa fa-check"></i>
                        OK
                      </button>
                      <button class="response-btn response-btn-not-ok" disabled>
                        <i class="fa fa-exclamation"></i>
                        Not OK
                      </button>
                    </div>
                  </div>

                  <div class="preview-footer">
                    <small
                      >This message will be sent to
                      {{ contacts.length + ccEmails.length }} recipients</small
                    >
                    <small
                      v-if="responseType === 'ok_not_ok'"
                      class="response-type-info"
                    >
                      <i class="fa fa-reply"></i>
                      Recipients can respond with OK/Not OK
                    </small>
                  </div>
                </div>
              </div>

              <!-- Message Details -->
              <div class="message-details-preview">
                <h4>Message Details</h4>
                <div class="details-grid">
                  <div class="detail-item">
                    <div class="detail-label">
                      <i class="fa fa-tag"></i> Message Type
                    </div>
                    <div class="detail-value">
                      {{ getMessageTypeName(selectedMessageType) || 'Not selected' }}
                    </div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">
                      <i class="fa fa-exclamation-triangle"></i> Crisis Type
                    </div>
                    <div class="detail-value">
                      {{ getCrisisTypeName(selectedCrisisType) || 'Not selected' }}
                    </div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">
                      <i class="fa fa-flag"></i> Priority
                    </div>
                    <div class="detail-value">
                      <span class="priority-badge" :class="'priority-' + messageType">
                        {{ messageType.charAt(0).toUpperCase() + messageType.slice(1) }}
                      </span>
                    </div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">
                      <i class="fa fa-reply"></i> Response Type
                    </div>
                    <div class="detail-value">
                      {{ responseType === 'ok_not_ok' ? 'OK/Not OK' : 'None' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recipients Preview -->
              <div class="recipients-preview">
                <h4>Recipients ({{ contacts.length + ccEmails.length }} total)</h4>
                <div class="recipients-chips-container">
                  <!-- Contact Recipients -->
                  <div v-if="contacts.length > 0">
                    <div class="recipients-section-label">
                      <i class="fa fa-users"></i> Contacts ({{ contacts.length }})
                    </div>
                    <div class="recipients-chips">
                      <span
                        v-for="contact in contacts"
                        :key="`preview-${contact.id}-${getContactLocationId(contact)}`"
                        class="recipient-chip"
                      >
                        <i class="fa fa-user"></i>
                        {{ contact.name }} - {{ contact.email }}
                      </span>
                    </div>
                  </div>

                  <!-- CC Email Recipients -->
                  <div v-if="ccEmails.length > 0" style="margin-top: 16px;">
                    <div class="recipients-section-label">
                      <i class="fa fa-envelope"></i> CC Emails ({{ ccEmails.length }})
                    </div>
                    <div class="recipients-chips">
                      <span
                        v-for="(email, index) in ccEmails"
                        :key="`cc-${index}`"
                        class="recipient-chip cc-chip"
                      >
                        <i class="fa fa-envelope-o"></i>
                        {{ email }}
                      </span>
                    </div>
                  </div>

                  <!-- No Recipients Message -->
                  <div v-if="contacts.length === 0 && ccEmails.length === 0" class="no-recipients">
                    <i class="fa fa-exclamation-circle"></i>
                    No recipients selected
                  </div>
                </div>
              </div>

              <!-- Send Options -->
              <div class="send-options">
                <h4>Send Options</h4>
                <div class="option-cards">
                  <div
                    class="option-card"
                    :class="{ active: selectedSendOption === 'send_now' }"
                    @click="selectedSendOption = 'send_now'"
                  >
                    <div class="option-icon">
                      <i class="fa fa-paper-plane"></i>
                    </div>
                    <div class="option-content">
                      <h5>Send Now</h5>
                      <p>
                        Send the message immediately to all selected recipients
                      </p>
                    </div>
                  </div>

                  <div
                    class="option-card disabled"
                    :class="{ active: selectedSendOption === 'send_scheduled' }"
                  >
                    <div class="option-icon">
                      <i class="fa fa-clock"></i>
                    </div>
                    <div class="option-content">
                      <h5>Schedule Send</h5>
                      <p>Schedule the message to be sent at a specific time</p>
                    </div>
                  </div>

                  <div
                    class="option-card disabled"
                    :class="{ active: selectedSendOption === 'draft' }"
                  >
                    <div class="option-icon">
                      <i class="fa fa-save"></i>
                    </div>
                    <div class="option-content">
                      <h5>Draft</h5>
                      <p>
                        Save the message as a draft for later review and sending
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step Controls -->
          <div class="step-controls">
            <div class="left-controls">
              <button
                class="btn btn-secondary"
                @click="previousStep"
                :disabled="currentStep === 0 || isSubmitting"
              >
                <i class="fa fa-arrow-left"></i>
                Back
              </button>
              <button
                class="btn btn-outline-danger"
                @click="cancelWizard"
                @mousedown="cancelWizard"
                type="button"
                :disabled="isSubmitting"
              >
                <i class="fa fa-times"></i>
                Cancel
              </button>

              <button
                class="btn btn-outline-warning"
                @click="modifyPolygon"
                @mousedown="modifyPolygon"
                type="button"
                :disabled="isSubmitting"
              >
                <i class="fa fa-draw-polygon"></i>
                Site Selection
              </button>
            </div>

            <button
              v-if="currentStep < wizardSteps.length - 1"
              class="btn btn-primary"
              @click="nextStep"
              :disabled="!canProceedComputed || isSubmitting"
            >
              Next
              <i class="fa fa-arrow-right"></i>
            </button>

            <button
              v-if="currentStep === wizardSteps.length - 1"
              class="btn btn-success"
              @click="completeWizard"
              :disabled="!canComplete() || isSubmitting"
            >
              <i v-if="isSubmitting" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-check"></i>
              {{ isSubmitting ? "Sending..." : "Complete" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import RequestBuilder from "../js/requestBuilder";
import LocationsWithContactsStep from "./wizard/steps/LocationsWithContactsStep.vue";
import CCRecipientsStep from "./wizard/steps/CCRecipientsStep.vue";
import ComposeMessageStep from "./wizard/steps/ComposeMessageStep.vue";
import PreviewSendStep from "./wizard/steps/PreviewSendStep.vue";

const requestBuilder = new RequestBuilder();
const MAX_BODY_LENGTH = 1400;

export default {
  name: "PolygonSelectionWizard",
  components: {
    LocationsWithContactsStep,
    CCRecipientsStep,
    ComposeMessageStep,
    PreviewSendStep,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    selectedSitesCount: {
      type: Number,
      default: 0,
    },
    selectedSites: {
      type: Array,
      default: () => [],
    },
    polygon: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentStep: 0,
      wizardSteps: [
        {
          title: "Compose Message",
          icon: "fa fa-edit",
        },
        {
          title: "Locations & Contacts",
          icon: "fa fa-users",
        },
        {
          title: "CC Recipients",
          icon: "fa fa-envelope",
        },
        {
          title: "Preview/Send",
          icon: "fa fa-check",
        },
      ],
      selectedSiteKeys: [],
      selectedAction: null,
      messageType: "medium",
      messageSubject: "",
      messageContent: "",
      messageContents: [], // New: Array of message contents in the new schema format
      maxBodyLength: MAX_BODY_LENGTH,
      responseType: "ok_not_ok",
      selectedSendOption: "send_scheduled", // Default to Send Scheduled
      selectedCrisisType: "", // Selected crisis type ID (required)
      crisisTypes: [], // List of crisis types from API
      fetchingCrisisTypes: false, // Loading state for crisis types
      selectedEvent: "", // Selected event ID (required)
      events: [], // List of events from API
      fetchingEvents: false, // Loading state for events
      templates: [], // List of templates from API
      loadingTemplates: false, // Loading state for templates
      selectedMessageType: "", // Selected message type code (required)
      messageTypes: [], // List of message types from API
      fetchingMessageTypes: false, // Loading state for message types
      schedulingRules: null, // Scheduling rules from API (object with priority levels)
      fetchingSchedulingRules: false, // Loading state for scheduling rules
      isGrouped: false, // Default to grouped view
      showTemplateDropdown: false,
      // Pagination properties
      currentPage: 1,
      itemsPerPage: 10,
      isPageTransitioning: false,
      // Office details
      officeDetails: {},
      fetchingOfficeDetails: false,
      isSubmitting: false,
      // Contacts
      contacts: [], // List of contacts from API
      fetchingContacts: false, // Loading state for contacts
      deletedContacts: [], // List of deleted contacts
      showingDeletedContacts: false, // DEPRECATED - use showingDeleted instead
      // Contacts pagination
      contactsCurrentPage: 1,
      contactsPerPage: 10,
      // CC Emails
      ccEmails: [], // List of CC email addresses
      newCcEmail: '', // Input for new CC email
      // Saved message ID
      savedMessageId: null, // Message ID returned after saving in Compose step
      // NEW: Additional properties for merged component
      showingDeleted: false, // Whether showing deleted contacts
      contactsSearchFilter: '', // Search filter for contacts in expanded view
      // Contact filters
      contactFilters: {
        name: '',
        email: '',
        cell: '',
        language: '',
        role: '',
        contactType: '',
        responseTeam: 'all' // 'all', 'yes', 'no'
      },
      messageTemplates: [
        {
          id: "safety_status_urgent",
          name: "Urgent Safety Check",
          subject: "URGENT: Safety Status Required - Environmental Alert",
          content:
            "Due to current environmental conditions in your area, we need to confirm your safety status immediately.\n\nEnvironmental Alert Details:\n- Severe weather conditions detected\n- Potential hazards in your operational area\n- Safety protocols may need activation\n\nPlease respond immediately to confirm:\nAre you and your team currently SAFE given the environmental circumstances?\n\nThis is an urgent safety check - immediate response required.",
          priority: "urgent",
          responseType: "ok_not_ok",
          crisisType: "0a4f4a79-08a6-f011-8e61-000d3a57b1fe", // Weather/Environmental Emergency
          messageType: "email", // Emergency message type code
        },
        {
          id: "weather_warning_high",
          name: "Weather Warning Alert",
          subject: "Weather Warning: Prepare for Severe Conditions",
          content:
            "A significant weather event is approaching your area. Please review and implement appropriate safety measures.\n\nWeather Alert Information:\n- High-risk weather conditions forecast\n- Potential impact on operations and personnel safety\n- Recommended precautionary measures should be taken\n\nPlease confirm that you have:\n1. Reviewed current weather conditions\n2. Implemented necessary safety protocols\n3. Secured equipment and facilities as needed\n\nStay safe and monitor conditions closely.",
          priority: "high",
          responseType: "ok_not_ok",
          crisisType: "0a4f4a79-08a6-f011-8e61-000d3a57b1fe", // Weather/Environmental Emergency
          messageType: "email", // Alert message type code
        },
        {
          id: "operational_status_medium",
          name: "Operational Status Update",
          subject: "Operational Status Check - Environmental Conditions",
          content:
            "We are monitoring environmental conditions in your operational area and need a status update.\n\nPlease provide information on:\n- Current operational status\n- Any weather-related impacts on activities\n- Equipment and personnel status\n- Any assistance needed\n\nThis information helps us maintain situational awareness and provide support as needed.\n\nThank you for your cooperation.",
          priority: "medium",
          responseType: "none",
          crisisType: "0a4f4a79-08a6-f011-8e61-000d3a57b1fe", // Operational Incident
          messageType: "email", // Update message type code
        },
        {
          id: "routine_check_low",
          name: "Routine Environmental Check",
          subject: "Routine Check: Environmental Conditions Update",
          content:
            "This is a routine check to gather information about current conditions in your area.\n\nPlease provide an update on:\n- Current weather conditions\n- Operational status\n- Any notable environmental changes\n- Equipment functionality\n\nThis routine information helps us maintain accurate situational awareness across all locations.\n\nPlease respond when convenient within the next 24 hours.",
          priority: "low",
          responseType: "none",
          crisisType: "0a4f4a79-08a6-f011-8e61-000d3a57b1fe", // Routine Check
          messageType: "email", // Information message type code
        },
      ],
    };
  },
  mounted() {
    // Initialize rich editor content
    this.$nextTick(() => {
      this.initializeRichEditor();
    });
    this.fetchOfficeDetails();
    this.fetchCrisisTypes();
    this.fetchMessageTypes();
    this.fetchSchedulingRules();
    this.fetchEvents();
    this.removeNonSelectableSites();
  },
  watch: {
    selectedSites: {
      immediate: true,
      handler(newSites) {
        // Initialize with only selectable sites selected
        const selectableSites = newSites.filter((site) =>
          this.isSiteSelectable(site)
        );
        this.selectedSiteKeys = selectableSites.map((site) => site.key);

        // Fetch office details when selectedSites changes
        if (newSites && newSites.length > 0) {
          this.fetchOfficeDetails();
        }
      },
    },
    officeDetails: {
      handler(newDetails) {
        // Fetch contacts when office details are updated
        if (newDetails && Object.keys(newDetails).length > 0) {
          this.fetchContacts();
        }
      },
    },
    currentStep: {
      handler(newStep) {
        // When navigating back to Compose Message step, restore the rich editor content
        if (newStep === 0) {
          this.$nextTick(() => {
            this.initializeRichEditor();
          });
        }
      },
    },
    contactFilters: {
      handler() {
        // Reset to first page when filters change
        this.contactsCurrentPage = 1;
      },
      deep: true,
    },
    selectedEvent: {
      handler(newEventId) {
        // Automatically set crisis type when event is selected
        if (newEventId) {
          const selectedEventObj = this.events.find(e => e.id === newEventId);
          if (selectedEventObj && selectedEventObj.crisisTypeId) {
            this.selectedCrisisType = selectedEventObj.crisisTypeId;
          }
        }
      },
    },
  },
  computed: {
    serverRoute() {
      return this.$store.getters.serverRoute;
    },
    mctServerRoute() {
      return this.$store.getters.mctServerRoute;
    },
    isAllSelected() {
      return (
        this.selectedSites.length > 0 &&
        this.selectedSiteKeys.length === this.selectedSites.length
      );
    },
    isIndeterminate() {
      return (
        this.selectedSiteKeys.length > 0 &&
        this.selectedSiteKeys.length < this.selectedSites.length
      );
    },
    groupedSites() {
      // Group sites by region ID
      const groups = {};
      this.selectedSites.forEach((site) => {
        const regionId = this.getSiteRegionId(site);
        if (!groups[regionId]) {
          groups[regionId] = [];
        }
        groups[regionId].push(site);
      });

      // Sort each group by site name
      Object.keys(groups).forEach((regionId) => {
        groups[regionId].sort((a, b) => {
          const nameA = (a.site.name || a.site.id).toLowerCase();
          const nameB = (b.site.name || b.site.id).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });
      return groups;
    },
    sortedSites() {
      // Return all sites sorted by name for non-grouped view
      return [...this.selectedSites].sort((a, b) => {
        const nameA = (a.site.name || a.site.id).toLowerCase();
        const nameB = (b.site.name || b.site.id).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },
    progressPercentage() {
      // Calculate progress percentage based on current step
      if (this.wizardSteps.length === 0) return 0;
      return Math.round(
        (this.currentStep / (this.wizardSteps.length - 1)) * 100
      );
    },
    // Pagination computed properties
    totalPages() {
      if (this.isGrouped) {
        return Math.ceil(
          Object.keys(this.groupedSites).length / this.itemsPerPage
        );
      } else {
        return Math.ceil(this.sortedSites.length / this.itemsPerPage);
      }
    },
    paginatedGroupedSites() {
      if (!this.isGrouped) return this.groupedSites;

      const groupKeys = Object.keys(this.groupedSites);
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      const paginatedKeys = groupKeys.slice(startIndex, endIndex);

      const paginatedGroups = {};
      paginatedKeys.forEach((key) => {
        paginatedGroups[key] = this.groupedSites[key];
      });

      return paginatedGroups;
    },
    paginatedSortedSites() {
      if (this.isGrouped) return this.sortedSites;

      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.sortedSites.slice(startIndex, endIndex);
    },
    filteredContacts() {
      if (!this.contacts || this.contacts.length === 0) {
        return [];
      }

      return this.contacts.filter(contact => {
        // First, filter out deleted contacts (check by id AND locationId)
        const isDeleted = this.deletedContacts.find(dc => this.contactsMatch(dc, contact));
        if (isDeleted) {
          return false;
        }

        // Filter by name
        if (this.contactFilters.name) {
          const name = (contact.name || '').toLowerCase();
          if (!name.includes(this.contactFilters.name.toLowerCase())) {
            return false;
          }
        }

        // Filter by email
        if (this.contactFilters.email) {
          const email = (contact.email || '').toLowerCase();
          if (!email.includes(this.contactFilters.email.toLowerCase())) {
            return false;
          }
        }

        // Filter by cell
        if (this.contactFilters.cell) {
          const cell = (contact.cell || '').toLowerCase();
          if (!cell.includes(this.contactFilters.cell.toLowerCase())) {
            return false;
          }
        }

        // Filter by language
        if (this.contactFilters.language) {
          const language = (contact.preferredLanguageCode || '').toLowerCase();
          if (!language.includes(this.contactFilters.language.toLowerCase())) {
            return false;
          }
        }

        // Filter by role
        if (this.contactFilters.role) {
          const roles = this.getContactRoles(contact).toLowerCase();
          if (!roles.includes(this.contactFilters.role.toLowerCase())) {
            return false;
          }
        }

        // Filter by contact type
        if (this.contactFilters.contactType) {
          const contactType = this.getContactTypes(contact).toLowerCase();
          if (!contactType.includes(this.contactFilters.contactType.toLowerCase())) {
            return false;
          }
        }

        // Filter by response team
        if (this.contactFilters.responseTeam !== 'all') {
          const isResponseTeam = contact.isResponseTeam;
          if (this.contactFilters.responseTeam === 'yes' && !isResponseTeam) {
            return false;
          }
          if (this.contactFilters.responseTeam === 'no' && isResponseTeam) {
            return false;
          }
        }

        return true;
      });
    },
    paginatedContacts() {
      const startIndex = (this.contactsCurrentPage - 1) * this.contactsPerPage;
      const endIndex = startIndex + this.contactsPerPage;
      return this.filteredContacts.slice(startIndex, endIndex);
    },
    contactsTotalPages() {
      return Math.ceil(this.filteredContacts.length / this.contactsPerPage);
    },
    hasActiveFilters() {
      return this.contactFilters.name !== '' ||
        this.contactFilters.email !== '' ||
        this.contactFilters.cell !== '' ||
        this.contactFilters.language !== '' ||
        this.contactFilters.role !== '' ||
        this.contactFilters.contactType !== '' ||
        this.contactFilters.responseTeam !== 'all';
    },
    // NEW: Group contacts by site key for the merged component
    contactsBySite() {
      const grouped = {};
      
      if (!this.contacts || this.contacts.length === 0) {
        return grouped;
      }
      this.contacts.forEach(contact => {
        // Get the site key - adjust this based on your actual data structure
        const siteKey = this.getSiteKeyForContact(contact);
        
        if (siteKey) {
          if (!grouped[siteKey]) {
            grouped[siteKey] = [];
          }
          // Only include contacts that haven't been deleted (check by both id and locationId)
          if (!this.deletedContacts.find(dc => this.contactsMatch(dc, contact))) {
            grouped[siteKey].push(contact);
          }
        }
      });
      
      return grouped;
    },
    // Active contacts (excluding deleted ones and from unselected locations)
    activeContacts() {
      if (!this.contacts || this.contacts.length === 0) {
        return [];
      }
      
      return this.contacts.filter(contact => {
        // Exclude deleted contacts
        if (this.deletedContacts.find(dc => this.contactsMatch(dc, contact))) {
          return false;
        }
        
        // Exclude contacts from unselected locations
        const locationId = this.getContactLocationId(contact);
        if (locationId) {
          // Find the site with this locationId
          const site = this.selectedSites.find(s => 
            s.office_details && s.office_details.id === locationId
          );
          
          // Only include if site exists and is selected
          if (!site || !this.selectedSiteKeys.includes(site.key)) {
            return false;
          }
        }
        
        return true;
      }).filter((contact, index, self) =>
        index === self.findIndex(c => c.id === contact.id)
      );
    },
    // Total contacts by site (including deleted) for showing count
    totalContactsBySite() {
      const grouped = {};
      
      // Count contacts from the main contacts array
      if (this.contacts && this.contacts.length > 0) {
        this.contacts.forEach(contact => {
          const siteKey = this.getSiteKeyForContact(contact);
          
          if (siteKey) {
            if (!grouped[siteKey]) {
              grouped[siteKey] = 0;
            }
            grouped[siteKey]++;
          }
        });
      }
      
      // Add deleted contacts to get the total count
      if (this.deletedContacts && this.deletedContacts.length > 0) {
        this.deletedContacts.forEach(contact => {
          const siteKey = this.getSiteKeyForContact(contact);
          
          if (siteKey) {
            if (!grouped[siteKey]) {
              grouped[siteKey] = 0;
            }
            grouped[siteKey]++;
          }
        });
      }
      
      return grouped;
    },
    // Deleted contacts grouped by site key
    deletedContactsBySite() {
      const grouped = {};
      
      if (!this.deletedContacts || this.deletedContacts.length === 0) {
        return grouped;
      }
      
      this.deletedContacts.forEach(contact => {
        const siteKey = this.getSiteKeyForContact(contact);
        
        if (siteKey) {
          if (!grouped[siteKey]) {
            grouped[siteKey] = [];
          }
          grouped[siteKey].push(contact);
        }
      });
      
      return grouped;
    },
    canProceedComputed() {
      switch (this.currentStep) {
        case 0:
          // Validate both English and French content
          const isValid = this.validateMessageContents();
          return isValid;
        case 1:
          return this.selectedSiteKeys.length > 0; // Must have impacted locations selected
        case 2:
          return true; // Recipients step - always can proceed if reached
        case 3:
          return true; // Preview step can complete
        default:
          return false;
      }
    },
    selectedEventCrisisTypeName() {
      if (!this.selectedEvent) return '';
      const event = this.events.find(e => e.id === this.selectedEvent);
      return event ? (event.crisisTypeName || '') : '';
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    resetWizardState() {
      // Reset wizard to initial state
      this.currentStep = 0;
      this.selectedSiteKeys = [];
      this.selectedAction = null;
      this.messageType = "medium";
      this.messageSubject = "";
      this.messageContent = "";
      this.messageContents = [];
      this.responseType = "ok_not_ok";
      this.selectedSendOption = "send_scheduled";
      this.selectedCrisisType = "";
      this.selectedEvent = "";
      this.selectedMessageType = "";
      this.isGrouped = false;
      this.showTemplateDropdown = false;
      this.currentPage = 1;
      this.isPageTransitioning = false;
      this.officeDetails = {};
      this.fetchingOfficeDetails = false;
      this.contacts = [];
      this.fetchingContacts = false;
      this.deletedContacts = [];
      this.showingDeleted = false;
      this.showingDeletedContacts = false; // DEPRECATED
      this.contactsSearchFilter = '';
      this.ccEmails = [];
      this.newCcEmail = '';
      this.savedMessageId = null;
      this.isSubmitting = false;
      this.clearContactFilters();

      // Clear rich editor content if it exists
      const richEditor = this.$refs.richEditor;
      if (richEditor && richEditor.innerHTML) {
        richEditor.innerHTML = "";
      }

      // Reset any form validations
      this.$nextTick(() => {
        // Clear any validation states
        if (this.$el && typeof this.$el.querySelector === "function") {
          const form = this.$el.querySelector("form");
          if (form && form.classList) {
            form.classList.remove("was-validated");
          }
        }
      });
    },
    handleOverlayClick() {
      // Disabled: Modal cannot be closed by clicking outside
      // this.closeModal()
    },
    goToStep(stepIndex) {
      // Only allow going back to previous steps, not forward
      if (stepIndex <= this.currentStep) {
        // Save message content before leaving step 0
        if (this.currentStep === 0) {
          this.updateMessageContent();
        }
        this.currentStep = stepIndex;
        this.scrollToTop();
      }
    },
    async nextStep() {
      if (!this.canProceed()) return;
      
      // Save message content before leaving step 0
      if (this.currentStep === 0) {
        this.updateMessageContent();
      }
      
      if (this.currentStep < this.wizardSteps.length - 1) {
        this.currentStep++;
        this.scrollToTop();
      }
    },
    previousStep() {
      // Save message content before leaving step 0
      if (this.currentStep === 0) {
        this.updateMessageContent();
      }
      
      if (this.currentStep > 0) {
        this.currentStep--;
        this.scrollToTop();
      }
    },
    scrollToTop() {
      // Scroll the modal container to the top
      this.$nextTick(() => {
        const modalContainer = this.$el.querySelector('.modal-container');
        if (modalContainer) {
          modalContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      });
    },
    canProceed() {
      switch (this.currentStep) {
        case 0:
          // Validate both English and French content
          const isValid = this.validateMessageContents();
          return isValid;
        case 1:
          return this.selectedSiteKeys.length > 0 && this.activeContacts.length > 0; // Must have impacted locations selected
        case 2:
          return true; // Recipients step - always can proceed if reached
        case 3:
          return true; // Preview step can complete
        default:
          return false;
      }
    },
    validateMessageContents() {
      // Check if messageContents has valid entries for both languages
      if (!this.messageContents || this.messageContents.length === 0) {
        return false;
      }
      
      // Check for required fields
      if (!this.selectedMessageType || !this.selectedMessageType.trim()) {
        return false;
      }
      if (!this.selectedCrisisType || !this.selectedCrisisType.trim()) {
        return false;
      }
      if (!this.selectedEvent || !this.selectedEvent.trim()) {
        return false;
      }
      
      // Check that we have both subject and body for English
      const enSubject = this.messageContents.find(
        mc => mc.languageCode === 'EN' && mc.contentType === 'message.subject'
      );
      const enBody = this.messageContents.find(
        mc => mc.languageCode === 'EN' && mc.contentType === 'message.body'
      );
      
      if (!enSubject || !enSubject.content || !enSubject.content.trim()) {
        return false;
      }
      
      const plainEnBody = this.getPlainTextFromHTML(enBody && enBody.content ? enBody.content : '');
      if (!plainEnBody || !plainEnBody.trim()) {
        return false;
      }
      
      // Check length limits for English
      if (enSubject.content.length > 200) {
        return false;
      }
      if (this.selectedMessageType.toLowerCase() !== 'email' && plainEnBody.length > this.maxBodyLength) {
        return false;
      }
      
      // Check that we have both subject and body for French
      const frSubject = this.messageContents.find(
        mc => mc.languageCode === 'FR' && mc.contentType === 'message.subject'
      );
      const frBody = this.messageContents.find(
        mc => mc.languageCode === 'FR' && mc.contentType === 'message.body'
      );
      
      if (!frSubject || !frSubject.content || !frSubject.content.trim()) {
        return false;
      }
      
      const plainFrBody = this.getPlainTextFromHTML(frBody && frBody.content ? frBody.content : '');
      if (!plainFrBody || !plainFrBody.trim()) {
        return false;
      }
      
      // Check length limits for French
      if (frSubject.content.length > 200) {
        return false;
      }
      if (this.selectedMessageType.toLowerCase() !== 'email' && plainFrBody.length > this.maxBodyLength) {
        return false;
      }
      
      return true;
    },
    canComplete() {
      return (
        this.validateMessageContents() &&
        this.selectedSiteKeys.length > 0 &&
        !this.isSubmitting
      );
    },
    canProceedToStep(stepIndex) {
      // Check if user can jump to a specific step
      for (let i = 0; i < stepIndex; i++) {
        if (!this.isStepValid(i)) {
          return false;
        }
      }
      return true;
    },
    isStepValid(stepIndex) {
      switch (stepIndex) {
        case 0:
          return this.validateMessageContents();
        case 1:
          return this.selectedSiteKeys.length > 0;
        case 2:
          return this.canComplete();
        default:
          return true;
      }
    },
    async completeWizard() {
      if (!this.canComplete()) return;

      // Save the message first if not already saved
      if (!this.savedMessageId) {
        await this.saveMessage();
        // Only proceed if message was saved successfully
        if (!this.savedMessageId) {
          return; // Stop if save failed
        }
      }

      // Get distinct contact IDs from the active contacts list (excluding deleted contacts)
      const contactIds = [...new Set(this.activeContacts.map(contact => contact.id))];
      
      // Now send the saved message
      this.sendSavedMessage(contactIds);
    },

    async sendSavedMessage(contactIds) {
      try {
        // Show loading state
        this.isSubmitting = true;

        // Make API call to send the saved message
        const [request, options] = await requestBuilder.sendMessageRequest(
          this.serverRoute,
          this.savedMessageId,
          contactIds
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Prepare wizard completion data for parent component
        const selectedSites = this.selectedSites.filter((site) =>
          this.selectedSiteKeys.includes(site.key)
        );

        const wizardData = {
          selectedSites,
          action: "message",
          polygon: this.polygon,
          messageType: this.messageType,
          messageSubject: this.messageSubject,
          messageContent: this.messageContent,
          responseType: this.responseType,
          selectedSendOption: this.selectedSendOption,
          messageId: this.savedMessageId,
          apiResult: result, // Include API response
        };

        // Emit success event
        this.$emit("wizard-complete", wizardData);

        // Emit event to clear polygons after successful message send
        this.$emit("clear-polygons");

        // Show success message
        this.showSuccessMessage(result);

        // Reset and close
        this.resetWizardState();
        this.closeModal();
      } catch (error) {
        console.error("Error sending message:", error);

        // Show error message to user
        this.showErrorMessage(error.message);

        // Don't close the modal so user can retry
      } finally {
        this.isSubmitting = false;
      }
    },

    async saveMessage() {
      try {
        // Show loading state
        this.isSubmitting = true;

        const selectedSites = this.selectedSites.filter((site) =>
          this.selectedSiteKeys.includes(site.key)
        );

        // Build recipient list from CC emails
        const allRecipients = [...new Set([...this.ccEmails])]; // Remove duplicates

        // Prepare API payload for saving message with new schema
        const messageData = {
          "eventId": this.selectedEvent,
          "messageTypeCode": this.selectedMessageType || "auto",
          "body": "OBSOLETE", // Kept for backward compatibility
          "responseTypeCode": this.responseType,
          "sendOptionCode": this.selectedSendOption,
          "subject": "OBSOLETE", // Kept for backward compatibility
          "recipientList": JSON.stringify(allRecipients),
          "priority": this.messageType,
          "crisisTypeId": this.selectedCrisisType,
          "locationIds": selectedSites.map(site => site.office_details.id),
          "messageContents": this.messageContents // New: Array of message contents
        };

        // Make API call to save message
        const [request, options] = await requestBuilder.createMessageRequest(
          this.serverRoute,
          messageData
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (response.status === 201) {
          const result = await response.json();
          
          // Store the message ID for later use when sending
          this.savedMessageId = result;
          
          if (!this.savedMessageId) {
            console.error('No message ID in response:', result);
            throw new Error('Message saved but no ID returned');
          }
          
        } else {
          throw new Error(`Unexpected status code: ${response.status}`);
        }
      } catch (error) {
        console.error("Error saving message:", error);
        
        // Show error message to user
        swal({
          title: "Message Save Failed",
          text: `There was an error saving your message:\n\n${error.message}\n\nPlease check your inputs and try again.`,
          icon: "error",
          button: "OK",
          dangerMode: true,
        });
        
        // Reset saved message ID on error
        this.savedMessageId = null;
      } finally {
        this.isSubmitting = false;
      }
    },

    showSuccessMessage(result) {
      swal({
        title: "Message Sent Successfully!",
        text: `Your message has been sent successfully to ${this.contacts.length} contact(s).${this.savedMessageId ? `\n\nMessage ID: ${this.savedMessageId}` : ''}`,
        icon: "success",
        button: "OK",
        timer: 10000,
      });
    },

    showErrorMessage(errorMessage) {
      swal({
        title: "Message Send Failed",
        text: `There was an error sending your message:\n\n${errorMessage}\n\nPlease try again.`,
        icon: "error",
        button: "OK",
        dangerMode: true,
      });
    },
    cancelWizard() {
      this.$emit("wizard-cancelled");
      this.resetWizardState();
      this.closeModal();
    },
    modifyPolygon() {
      this.$emit("modify-polygon");
      this.closeModal();
    },
    toggleSiteSelection(siteId) {
      const index = this.selectedSiteKeys.indexOf(siteId);
      if (index > -1) {
        this.selectedSiteKeys.splice(index, 1);
      } else {
        this.selectedSiteKeys.push(siteId);
      }
    },
    selectAllSites() {
      this.selectedSiteKeys = this.selectedSites.map((site) => site.key);
    },
    deselectAllSites() {
      this.selectedSiteKeys = [];
    },
    toggleAllSites() {
      if (this.isAllSelected) {
        this.deselectAllSites();
      } else {
        this.selectAllSites();
      }
    },
    toggleGrouping() {
      this.isGrouped = !this.isGrouped;
      this.currentPage = 1; // Reset to first page when toggling view
    },
    // Pagination methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
      }
    },
    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    isRegionSelected(regionId) {
      const regionSites = this.groupedSites[regionId];
      if (!regionSites || regionSites.length === 0) return false;

      const selectableRegionSites = regionSites.filter((site) =>
        this.isSiteSelectable(site)
      );
      if (selectableRegionSites.length === 0) return false;

      const selectableRegionSiteKeys = selectableRegionSites.map(
        (site) => site.key
      );
      return selectableRegionSiteKeys.every((key) =>
        this.selectedSiteKeys.includes(key)
      );
    },
    isRegionIndeterminate(regionId) {
      const regionSites = this.groupedSites[regionId];
      if (!regionSites || regionSites.length === 0) return false;

      const selectableRegionSites = regionSites.filter((site) =>
        this.isSiteSelectable(site)
      );
      if (selectableRegionSites.length === 0) return false;

      const selectableRegionSiteKeys = selectableRegionSites.map(
        (site) => site.key
      );
      const selectedInRegion = selectableRegionSiteKeys.filter((key) =>
        this.selectedSiteKeys.includes(key)
      );

      return (
        selectedInRegion.length > 0 &&
        selectedInRegion.length < selectableRegionSiteKeys.length
      );
    },
    toggleRegionSelection(regionId) {
      const regionSites = this.groupedSites[regionId];
      if (!regionSites || regionSites.length === 0) return;

      const selectableRegionSites = regionSites.filter((site) =>
        this.isSiteSelectable(site)
      );
      const selectableRegionSiteKeys = selectableRegionSites.map(
        (site) => site.key
      );
      const isRegionFullySelected = this.isRegionSelected(regionId);

      if (isRegionFullySelected) {
        // Deselect all selectable sites in this region
        selectableRegionSiteKeys.forEach((key) => {
          const index = this.selectedSiteKeys.indexOf(key);
          if (index > -1) {
            this.selectedSiteKeys.splice(index, 1);
          }
        });
      } else {
        // Select all selectable sites in this region
        selectableRegionSiteKeys.forEach((key) => {
          if (!this.selectedSiteKeys.includes(key)) {
            this.selectedSiteKeys.push(key);
          }
        });
      }
    },
    getSelectedSitesCount() {
      return this.selectedSiteKeys.length;
    },
    getActionName() {
      const actions = {
        message: "Compose Message",
        export: "Export Data",
        analyze: "Analyze Risks",
      };
      return actions[this.selectedAction] || "No action selected";
    },
    getSiteCode(site) {
      return site && site.site && site.site.id ? site.site.id : "Unknown Code";
    },
    isSiteSelectable(site) {
      return (
        site && site.office_details && site.office_details.isActive
      );
    },
    getSiteAddress(site) {
      return '-'; // temporarily disable address display
      if (!site || !site.office_details) return "Unknown Address";
      const city = site.office_details.city ? site.office_details.city : "";
      const province_code = site.office_details.province_code
        ? site.office_details.province_code
        : "";
      const address = site.office_details.address
        ? site.office_details.address
        : "";
      return (
        `${address}, ${city}, ${province_code}`
          .replace(/^,\s*|,\s*$/g, "")
          .replace(/,\s*,/g, ",") || "Unknown Address"
      );
    },
    getSiteId(site) {
      return site && site.id
        ? site.id
        : "Unknown ID";
    },
    getOfficeLeader(site) {
      return '-'; // temporarily disable email display
      if (!site || !site.office_details) return "Unknown Leader";
      return site.office_details.office_leader_email || "Unknown Leader";
    },
    removeNonSelectableSites() {
      // Filter out non-selectable sites from current selection
      const selectableSiteKeys = this.selectedSiteKeys.filter((key) => {
        const site = this.selectedSites.find((s) => s.key === key);
        return site && this.isSiteSelectable(site);
      });

      // Update selectedSiteKeys if any non-selectable sites were found
      if (selectableSiteKeys.length !== this.selectedSiteKeys.length) {
        this.selectedSiteKeys = selectableSiteKeys;
      }
    },
    getSiteRegionId(site) {
      // Handle both formats: site.region.id and site.site.region.id
      if (site && site.site && site.site.region && site.site.region.id) {
        return site.site.region.id;
      }
      if (site && site.region && site.region.id) {
        return site.region.id;
      }
      if (site && site.site && site.site.regionId) {
        return site.site.regionId;
      }
      if (site && site.regionId) {
        return site.regionId;
      }
      return "Unknown Region";
    },
    getRiskClass(riskLevel) {
      if (riskLevel >= 4) return "risk-high";
      if (riskLevel >= 2) return "risk-medium";
      return "risk-low";
    },
    getPolygonArea() {
      if (!this.polygon || !this.polygon._latlngs) return 0;
      return this.polygon.getArea ? this.polygon.getArea() : 0;
    },
    getPolygonPerimeter() {
      if (!this.polygon || !this.polygon._latlngs) return 0;
      let perimeter = 0;
      const latlngs = this.polygon._latlngs[0] || this.polygon._latlngs;
      for (let i = 0; i < latlngs.length; i++) {
        const current = latlngs[i];
        const next = latlngs[(i + 1) % latlngs.length];
        perimeter += current.distanceTo(next);
      }
      return perimeter;
    },
    applyTemplate(template) {
      this.messageSubject = template.subject;
      this.messageType = template.priority;
      this.responseType = template.responseType;
      this.showTemplateDropdown = false;

      // Convert plain text to HTML with line breaks
      const htmlContent = template.content.replace(/\n/g, "<br>");

      // Update the rich editor content
      this.$nextTick(() => {
        if (this.$refs.richEditor) {
          this.$refs.richEditor.innerHTML = htmlContent;
          this.messageContent = htmlContent;
          this.$refs.richEditor.focus();
          this.placeCursorAtEnd();
        }
      });
    },
    getSelectedSitesData() {
      return this.selectedSites.filter((site) =>
        this.selectedSiteKeys.includes(site.key)
      );
    },
    getSelectedGroupedSites() {
      const selectedSites = this.getSelectedSitesData();
      const groups = {};

      selectedSites.forEach((site) => {
        const regionId = this.getSiteRegionId(site);
        if (!groups[regionId]) {
          groups[regionId] = [];
        }
        groups[regionId].push(site);
      });

      return groups;
    },
    getSelectedGroupedSitesFlat() {
      const groups = this.getSelectedGroupedSites();
      const flattened = [];

      Object.keys(groups).forEach((regionId) => {
        // Add region header
        flattened.push({
          key: `header-${regionId}`,
          type: "header",
          regionId: regionId,
          count: groups[regionId].length,
        });

        // Add sites in this region
        groups[regionId].forEach((site) => {
          flattened.push({
            key: `site-${regionId}-${site.key}`,
            type: "site",
            site: site,
            regionId: regionId,
          });
        });
      });

      return flattened;
    },
    getUniqueOfficeLeaderEmails() {
      const selectedSites = this.getSelectedSitesData();
      const emails = selectedSites
        .map((site) => this.getOfficeLeader(site))
        .filter((email) => email && email !== "Unknown Leader");

      // Return unique emails
      return [...new Set(emails)];
    },
    getUniqueRecipientObjects() {
      const selectedSites = this.getSelectedSitesData();
      const recipients = selectedSites
        .map((site) => {
          const email = this.getOfficeLeader(site);
          const officeId = this.getSiteId(site);
          return {
            office_id: officeId,
            recipient_email: email,
          };
        })
        .filter(
          (recipient) =>
            recipient.recipient_email &&
            recipient.recipient_email !== "Unknown Leader" &&
            recipient.office_id &&
            recipient.office_id !== "Unknown ID"
        );

      // Remove duplicates based on email and office_id combination
      const uniqueRecipients = [];
      const seen = new Set();

      recipients.forEach((recipient) => {
        const key = `${recipient.office_id}-${recipient.recipient_email}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueRecipients.push(recipient);
        }
      });

      return uniqueRecipients;
    },
    getUniqueRecipients() {
      const selectedSites = this.getSelectedSitesData();
      const recipientsMap = new Map();

      selectedSites.forEach((site) => {
        const email = this.getOfficeLeader(site);
        const officeDetails = this.getOfficeDetails(site);
        const siteName = site.site.name || site.site.id;

        if (email && email !== "Unknown Leader") {
          if (recipientsMap.has(email)) {
            // Increment site count for existing recipient
            recipientsMap.get(email).siteCount++;
          } else {
            // Add new recipient
            recipientsMap.set(email, {
              name: officeDetails && officeDetails.office_leader_name ? officeDetails.office_leader_name : 'N/A',
              email: email,
              phone: officeDetails && officeDetails.office_leader_phone ? officeDetails.office_leader_phone : 'N/A',
              siteCount: 1,
            });
          }
        }
      });

      return Array.from(recipientsMap.values());
    },
    getUniqueRecipientsCount() {
      return this.getUniqueRecipients().length;
    },
    getPriorityIcon(priority) {
      const icons = {
        low: "fa fa-info-circle",
        medium: "fa fa-exclamation-triangle",
        high: "fa fa-exclamation-circle",
        urgent: "fa fa-exclamation",
      };
      return icons[priority] || "fa fa-info-circle";
    },
    getMessageTypeName(code) {
      if (!code) return '';
      const messageType = this.messageTypes.find(mt => mt.code === code);
      return messageType ? messageType.name : code;
    },
    getCrisisTypeName(id) {
      if (!id) return '';
      const crisisType = this.crisisTypes.find(ct => ct.id === id);
      return crisisType ? crisisType.name : id;
    },
    getEventName(id) {
      if (!id) return '';
      const event = this.events.find(e => e.id === id);
      return event ? event.name : id;
    },
    // Rich Text Editor Methods
    formatText(command) {
      document.execCommand(command, false, null);
      this.updateMessageContent();
      this.$refs.richEditor.focus();
    },
    isFormatActive(command) {
      return document.queryCommandState(command);
    },
    updateMessageContent() {
      if (this.$refs.richEditor) {
        this.messageContent = this.$refs.richEditor.innerHTML;
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData(
        "text/plain"
      );
      document.execCommand("insertText", false, text);
      this.updateMessageContent();
    },
    handleKeydown(event) {
      // Prevent exceeding character limit
      const plainText = this.getPlainTextFromHTML(this.messageContent);
      if (plainText.length >= 2000 && !this.isNavigationKey(event)) {
        event.preventDefault();
      }
    },
    isNavigationKey(event) {
      const navKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      return navKeys.includes(event.key) || event.ctrlKey || event.metaKey;
    },
    getPlainTextLength() {
      return this.getPlainTextFromHTML(this.messageContent).length;
    },
    getPlainTextFromHTML(html) {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return div.textContent || div.innerText || "";
    },
    setRichEditorContent(content) {
      if (this.$refs.richEditor) {
        // Save cursor position
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        // Set content
        this.$refs.richEditor.innerHTML = content || "";
        this.messageContent = content || "";

        // Restore cursor position if editor was focused
        if (range && document.activeElement === this.$refs.richEditor) {
          try {
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            // If range is invalid, place cursor at end
            this.placeCursorAtEnd();
          }
        }
      }
    },
    placeCursorAtEnd() {
      if (this.$refs.richEditor) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(this.$refs.richEditor);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
    // Fetch office details and merge with sites data
    async fetchOfficeDetails() {
      if (this.fetchingOfficeDetails) return;

      this.fetchingOfficeDetails = true;

      try {
        // Extract unique office codes from selected sites
        const officeCodes = [
          ...new Set(this.selectedSites.map((site) => this.getSiteCode(site))),
        ].filter((code) => code && code !== "Unknown Code");
        if (officeCodes.length === 0) {
          return;
        }

        // Make HTTP request to fetch office details
        const [
          request,
          options,
        ] = await requestBuilder.getOfficeDetailsByCodesRequest(
          this.serverRoute,
          officeCodes
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.officeDetails = await response.json();
        this.addOfficeDetailsToSites();
      } catch (error) {
        console.error("Error fetching office details:", error);
        // Continue without office details
      } finally {
        this.fetchingOfficeDetails = false;
      }
    },

    // Add office details directly to the selectedSites array
    addOfficeDetailsToSites() {
      this.selectedSites.forEach((site) => {
        const officeCode = this.getSiteCode(site);
        const officeDetail = this.officeDetails.find(od => od.code === officeCode) || null;

        // Direct assignment works with Vue 3 Proxy-based reactivity
        site.office_details = officeDetail;
      });

    },

    // Get office details for display
    getOfficeDetails(site) {
      // Return office details from the site object directly
      return site.office_details || null;
    },

    // Fetch contacts by location IDs from the MCT API
    async fetchContacts() {
      if (this.fetchingContacts) return;

      this.fetchingContacts = true;

      try {
        // Extract unique location IDs from selected sites with office details
        const locationIds = [
          ...new Set(
            this.selectedSites
              .filter(site => site.office_details && site.office_details.id)
              .map(site => site.office_details.id)
          )
        ];

        if (locationIds.length === 0) {
          this.contacts = [];
          return;
        }

        // Make HTTP request to fetch contacts
        const [request, options] = await requestBuilder.getContactsByLocations(
          this.serverRoute,
          locationIds
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the contacts array
        if (Array.isArray(result)) {
          this.contacts = result;
        } else {
          console.warn("Unexpected contacts response format:", result);
          this.contacts = [];
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
        this.contacts = [];
      } finally {
        this.fetchingContacts = false;
      }
    },

    // Get contact role names from locationRoles array
    getContactRoles(contact) {
      if (!contact.locationRoles || contact.locationRoles.length === 0) {
        return 'N/A';
      }
      
      // Extract unique role names and join them
      const roleNames = contact.locationRoles
        .map(lr => lr.roleName)
        .filter(name => name); // Filter out null/undefined
      
      // If multiple roles, join with commas
      return roleNames.length > 0 ? roleNames.join(', ') : 'N/A';
    },

    // Get contact type descriptions from locationRoles array
    getContactTypes(contact) {
      if (!contact.locationRoles || contact.locationRoles.length === 0) {
        return 'N/A';
      }
      
      // Extract unique contact type descriptions and join them
      const contactTypes = contact.locationRoles
        .map(lr => lr.contactTypeDescription || lr.contactTypeCode)
        .filter(type => type); // Filter out null/undefined
      
      // Remove duplicates and join with commas
      const uniqueTypes = [...new Set(contactTypes)];
      return uniqueTypes.length > 0 ? uniqueTypes.join(', ') : 'N/A';
    },

    // Clear all contact filters
    clearContactFilters() {
      this.contactFilters = {
        name: '',
        email: '',
        cell: '',
        language: '',
        role: '',
        contactType: '',
        responseTeam: 'all'
      };
    },

    // NEW: Get site key for a site object
    getSiteKey(site) {
      return site.key;
    },

    // NEW: Get site key for a contact
    getSiteKeyForContact(contact) {
      // Match contact to site by office details ID using the contact's locationId
      if (contact.locationId) {
        const locationId = contact.locationId;
        
        // Find the site that has this locationId
        const site = this.selectedSites.find(s => 
          s.office_details && s.office_details.id === locationId
        );
        
        if (site) {
          return this.getSiteKey(site);
        }
      }
      return null;
    },

    // Helper method to get locationId from a contact
    getContactLocationId(contact) {
      // Each contact object has a locationId property
      if (!contact) return null;
      return contact.locationId || null;
    },

    // Helper method to check if two contacts match (by id and locationId)
    contactsMatch(contact1, contact2) {
      if (!contact1 || !contact2) return false;
      const loc1 = this.getContactLocationId(contact1);
      const loc2 = this.getContactLocationId(contact2);
      return contact1.id === contact2.id && loc1 === loc2;
    },

    // Remove a contact from the list
    removeContact(contactId, locationId) {
      const index = this.contacts.findIndex(contact => {
        const contactLocationId = this.getContactLocationId(contact);
        const matches = contact.id === contactId && contactLocationId === locationId;
        return matches;
      });
      
      if (index !== -1) {
        const contact = this.contacts[index];
        this.deletedContacts.push(contact);
        this.contacts.splice(index, 1);
      }
    },

    // Restore a deleted contact
    restoreContact(contactId, locationId) {
      const index = this.deletedContacts.findIndex(contact => {
        const contactLocationId = this.getContactLocationId(contact);
        const matches = contact.id === contactId && contactLocationId === locationId;
        return matches;
      });
      
      if (index !== -1) {
        const contact = this.deletedContacts[index];
        this.contacts.push(contact);
        this.deletedContacts.splice(index, 1);
        
        // Hide deleted contacts section if no more deleted contacts
        if (this.deletedContacts.length === 0) {
          this.showingDeleted = false;
        }
      }
    },

    // Show deleted contacts section
    showDeletedContacts() {
      this.showingDeleted = true;
    },

    // Hide deleted contacts section
    hideDeletedContacts() {
      this.showingDeleted = false;
    },

    // Validate email format
    isValidEmail(email) {
      if (!email || typeof email !== 'string') return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    },

    // Add CC email
    addCcEmail() {
      const email = this.newCcEmail.trim().toLowerCase();
      if (this.isValidEmail(email)) {
        // Check for duplicates
        if (!this.ccEmails.includes(email)) {
          this.ccEmails.push(email);
          this.newCcEmail = '';
        } else {
          swal({
            title: "Duplicate Email",
            text: "This email address is already in the CC list.",
            icon: "warning",
            button: "OK",
          });
        }
      } else {
        swal({
          title: "Invalid Email",
          text: "Please enter a valid email address.",
          icon: "error",
          button: "OK",
        });
      }
    },

    // Remove CC email
    removeCcEmail(index) {
      if (index >= 0 && index < this.ccEmails.length) {
        this.ccEmails.splice(index, 1);
      }
    },

    // Format office information for display
    formatOfficeInfo(officeDetails) {
      if (!officeDetails) return "";
      return officeDetails.office_leader_email;
      const parts = [];
      if (officeDetails.office_name) parts.push(officeDetails.office_name);
      if (officeDetails.office_leader_email)
        parts.push(officeDetails.office_leader_email);
      if (officeDetails.province_code) parts.push(officeDetails.province_code);

      return parts.join(", ");
    },

    initializeRichEditor() {
      if (this.$refs.richEditor) {
        // Set the content from messageContent if it exists
        if (this.messageContent) {
          this.$refs.richEditor.innerHTML = this.messageContent;
        } else {
          // Clear the editor if no content
          this.$refs.richEditor.innerHTML = "";
        }
      }
    },

    // Fetch crisis types from the MCT API
    async fetchCrisisTypes() {
      if (this.fetchingCrisisTypes) return;

      this.fetchingCrisisTypes = true;

      try {
        const [request, options] = await requestBuilder.getCrisisTypes(
          this.serverRoute
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the crisis types array (user must select)
        if (Array.isArray(result)) {
          this.crisisTypes = result;
          // Don't auto-select - require user to choose
          if (!this.selectedCrisisType && this.crisisTypes.length === 0) {
            this.selectedCrisisType = "";
          }
        } else {
          console.warn("Unexpected crisis types response format:", result);
          this.crisisTypes = [];
          this.selectedCrisisType = "";
        }
      } catch (error) {
        console.error("Error fetching crisis types:", error);
        this.crisisTypes = [];
      } finally {
        this.fetchingCrisisTypes = false;
      }
    },

    // Fetch message types from the MCT API
    async fetchMessageTypes() {
      if (this.fetchingMessageTypes) return;

      this.fetchingMessageTypes = true;

      try {
        const [request, options] = await requestBuilder.getMessageTypes(
          this.serverRoute
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the message types array
        if (Array.isArray(result)) {
          this.messageTypes = result;
        } else {
          console.warn("Unexpected message types response format:", result);
          this.messageTypes = [];
        }
      } catch (error) {
        console.error("Error fetching message types:", error);
        this.messageTypes = [];
      } finally {
        this.fetchingMessageTypes = false;
      }
    },

    // Fetch scheduling rules from the MCT API
    async fetchSchedulingRules() {
      if (this.fetchingSchedulingRules) return;

      this.fetchingSchedulingRules = true;

      try {
        const [request, options] = await requestBuilder.getSchedulingRules(
          this.serverRoute
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the scheduling rules object
        if (result && typeof result === 'object') {
          this.schedulingRules = result;
        } else {
          console.warn("Unexpected scheduling rules response format:", result);
          this.schedulingRules = null;
        }
      } catch (error) {
        console.error("Error fetching scheduling rules:", error);
        this.schedulingRules = null;
      } finally {
        this.fetchingSchedulingRules = false;
      }
    },

    // Fetch events from the MCT API
    async fetchEvents() {
      if (this.fetchingEvents) return;

      this.fetchingEvents = true;

      try {
        const [request, options] = await requestBuilder.getEvents(
          this.serverRoute
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the events array
        if (Array.isArray(result)) {
          this.events = result;
        } else {
          console.warn(
            `Expected array of events but received: ${typeof result}. First item: ${JSON.stringify(result && result[0])}`,
            result
          );
          this.events = [];
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        this.events = [];
      } finally {
        this.fetchingEvents = false;
      }
    },

    // Fetch templates by crisis type from the MCT API
    async fetchTemplates(crisisTypeId) {
      if (this.loadingTemplates) return;
      if (!crisisTypeId) {
        console.warn("No crisis type ID provided for fetching templates");
        return;
      }

      this.loadingTemplates = true;

      try {
        const [request, options] = await requestBuilder.getTemplatesByCrisisType(
          this.serverRoute,
          crisisTypeId
        );
        const response = await fetch(request, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store the templates array
        if (Array.isArray(result)) {
          this.templates = result;
        } else {
          console.warn(
            `Expected array of templates but received: ${typeof result}. First item: ${JSON.stringify(result && result[0])}`,
            result
          );
          this.templates = [];
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        this.templates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 1200px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 16px 16px 12px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 16px;
  min-height: 300px;
  overflow-x: hidden;
  background: linear-gradient(
    135deg,
    rgba(247, 250, 252, 0.8) 0%,
    rgba(241, 245, 249, 0.9) 50%,
    rgba(248, 250, 252, 0.8) 100%
  );
}

/* Custom Step Wizard Styles */
.step-wizard {
  min-height: 300px;
}

.step-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
  padding: 12px 0 16px 0;
}

.step-nav::before {
  content: "";
  position: absolute;
  top: 44px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 130px);
  height: 4px;
  background: linear-gradient(90deg, #f1f3f4 0%, #e8eaed 100%);
  border-radius: 2px;
  z-index: 1;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.08);
}

.step-nav::after {
  content: "";
  position: absolute;
  top: 44px;
  left: 50%;
  transform-origin: left center;
  width: calc(100% - 130px);
  height: 4px;
  background: linear-gradient(90deg, #4285f4 0%, #1a73e8 50%, #1557b0 100%);
  border-radius: 2px;
  z-index: 2;
  transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 1px 4px rgba(66, 133, 244, 0.4);
}

/* Progress bar states for 4 steps (0, 1, 2, 3) */
.step-nav[data-progress="0"]::after {
  transform: translateX(-48%) scaleX(0);
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
}
.step-nav[data-progress="1"]::after {
  transform: translateX(-48%) scaleX(0.333);
  box-shadow: 0 2px 12px rgba(66, 133, 244, 0.5);
}
.step-nav[data-progress="2"]::after {
  transform: translateX(-48%) scaleX(0.667);
  box-shadow: 0 2px 16px rgba(26, 115, 232, 0.6);
}
.step-nav[data-progress="3"]::after {
  transform: translateX(-48%) scaleX(1);
  box-shadow: 0 2px 16px rgba(26, 115, 232, 0.6);
}

.step-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 3;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  padding: 8px 6px;
  border-radius: 12px;
  min-width: 80px;
}

.step-nav-item:hover:not(.disabled) {
  transform: translateY(-4px);
  background: rgba(66, 133, 244, 0.04);
}

.step-nav-item.disabled {
  cursor: not-allowed;
}

.step-nav-item.active {
  background: rgba(66, 133, 244, 0.08);
}

.step-nav-item.completed {
  background: rgba(52, 168, 83, 0.06);
}

.step-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 8px;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: 2px solid #e8eaed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
}

.step-circle::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: 50%;
  /* opacity: 0; */
  transition: opacity 0.4s ease;
}

.step-nav-item:hover:not(.disabled) .step-circle {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);
}

.step-nav-item:hover:not(.disabled) .step-circle::before {
  opacity: 1;
}

.step-nav-item.active .step-circle {
  background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
  color: white;
  border-color: #1557b0;
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(66, 133, 244, 0.4),
    0 2px 8px rgba(26, 115, 232, 0.3), 0 0 0 4px rgba(66, 133, 244, 0.1);
  animation: activePulse 2.5s infinite ease-in-out;
}

.step-nav-item.completed .step-circle {
  background: linear-gradient(135deg, #34a853 0%, #137333 100%);
  color: white;
  border-color: #0d652d;
  box-shadow: 0 3px 12px rgba(52, 168, 83, 0.3),
    0 1px 4px rgba(19, 115, 51, 0.2);
}

.step-nav-item.completed .step-circle i {
  font-size: 16px;
  font-weight: 900;
}

@keyframes activePulse {
  0% {
    box-shadow: 0 4px 16px rgba(66, 133, 244, 0.4),
      0 2px 8px rgba(26, 115, 232, 0.3), 0 0 0 4px rgba(66, 133, 244, 0.1);
  }
  50% {
    box-shadow: 0 6px 20px rgba(66, 133, 244, 0.5),
      0 3px 10px rgba(26, 115, 232, 0.4), 0 0 0 6px rgba(66, 133, 244, 0.15);
  }
  100% {
    box-shadow: 0 4px 16px rgba(66, 133, 244, 0.4),
      0 2px 8px rgba(26, 115, 232, 0.3), 0 0 0 4px rgba(66, 133, 244, 0.1);
  }
}

.step-title {
  font-size: 11px;
  color: #5f6368;
  text-align: center;
  font-weight: 600;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  letter-spacing: 0.2px;
  text-transform: uppercase;
  margin-top: 4px;
  line-height: 1.2;
}

.step-nav-item.active .step-title {
  color: #1a73e8;
  font-weight: 700;
  transform: translateY(-2px);
}

.step-nav-item.completed .step-title {
  color: #137333;
  font-weight: 700;
}

.step-nav-item:hover:not(.disabled) .step-title {
  color: #1a73e8;
  transform: translateY(-2px);
}

.step-content-container {
  min-height: 250px;
  margin-bottom: 16px;
  overflow-x: hidden;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.step-content-wrapper {
  width: 100%;
}

.step-content {
  padding: 10px 0;
  animation: slideInFade 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-x: hidden;
}

@keyframes slideInFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 10px;
  border-top: 2px solid rgba(0, 123, 255, 0.1);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(248, 249, 250, 0.8) 100%
  );
  border-radius: 12px;
  margin-top: 12px;
}

.left-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.step-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(0.4);
  background: #e9ecef !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  transform: none !important;
  box-shadow: none !important;
}

.step-controls .btn:not(:disabled) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step-controls .btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.step-controls .btn:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
  cursor: not-allowed;
}

/* Message Composer Styles */
.compose-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 20px;
}

.compose-header h3 {
  margin: 0;
  flex: 1;
}

.template-section {
  position: relative;
  flex-shrink: 0;
}

.templates-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.templates-btn:hover {
  border-color: #007bff;
  color: #007bff;
  background: #f8f9fa;
}

.template-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 320px;
  max-width: 400px;
}

.template-dropdown-header {
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
}

.template-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.template-item:last-child {
  border-bottom: none;
  border-radius: 0 0 8px 8px;
}

.template-item:hover {
  background: #f8f9fa;
}

.template-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  font-size: 14px;
}

.template-preview {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}
.selection-summary-compact {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495057;
  font-weight: 500;
}

.summary-item i {
  color: #007bff;
  width: 16px;
}

/* Recipients Step Styles */
.recipients-container {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.recipients-summary {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #007bff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.recipients-count {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.clear-filters-btn {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clear-filters-btn:hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.recipients-table-wrapper {
  overflow-x: hidden;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.recipients-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 14px;
}

.recipients-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.recipients-table th {
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #e0e0e0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipients-table .filter-row th {
  padding: 8px;
  background: #ffffff;
  border-bottom: 2px solid #e0e0e0;
}

.filter-input,
.filter-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.filter-input::placeholder {
  color: #adb5bd;
  font-size: 12px;
}

.filter-select {
  cursor: pointer;
  background-color: white;
  font-size: 12px;
}

.recipients-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.recipients-table tbody tr:hover {
  background-color: #f8f9fa;
}

.recipients-table td {
  padding: 12px 8px;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Recipients table column widths - Fixed percentages, no min-width */
.recipients-table .name-column,
.recipients-table .name-cell {
  width: 14%;
}

.recipients-table .email-column,
.recipients-table .email-cell {
  width: 20%;
}

.recipients-table .phone-column,
.recipients-table .phone-cell {
  width: 11%;
}

.recipients-table .language-column,
.recipients-table .language-cell {
  width: 7%;
  text-align: center;
}

.recipients-table .role-column,
.recipients-table .role-cell {
  width: 20%;
}

.recipients-table .contact-type-column,
.recipients-table .contact-type-cell {
  width: 11%;
  text-align: center;
}

.recipients-table .response-team-column,
.recipients-table .response-team-cell {
  width: 10%;
  text-align: center;
}

.recipients-table .actions-column,
.recipients-table .actions-cell {
  width: 7%;
  text-align: center;
}

.name-cell,
.email-cell {
  color: #2c3e50;
}

.phone-cell {
  color: #6c757d;
}

.language-cell {
  font-size: 13px;
  text-transform: uppercase;
  color: #6c757d;
}

.role-cell {
  color: #2c3e50;
  font-size: 13px;
}

.contact-type-cell {
  font-size: 12px;
  color: #495057;
  font-weight: 500;
}

.response-team-cell .badge {
  font-size: 11px;
  padding: 4px 8px;
  font-weight: 500;
}

/* Contacts Pagination */
.contacts-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  border-radius: 0 0 6px 6px;
}

.pagination-info {
  font-size: 13px;
  color: #6c757d;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-current {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  padding: 0 12px;
}

.pagination-controls .btn {
  min-width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}


.actions-cell {
  text-align: center;
}

.btn-remove-contact {
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-remove-contact:hover {
  background-color: #dc3545;
  color: white;
  transform: scale(1.1);
}

.btn-remove-contact:active {
  transform: scale(0.95);
}

.btn-remove-contact i {
  pointer-events: none;
}

/* Deleted Contacts Section */
.deleted-contacts-section {
  margin-top: 24px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 16px;
}

.deleted-contacts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ffc107;
}

.deleted-contacts-header h4 {
  margin: 0;
  color: #856404;
  font-size: 16px;
  font-weight: 600;
}

.deleted-contacts-table-wrapper {
  overflow-x: auto;
  border: 1px solid #ffc107;
  border-radius: 6px;
  background: white;
}

.deleted-row {
  background-color: #fffbf0 !important;
  opacity: 0.85;
}

.deleted-row:hover {
  background-color: #fff3cd !important;
  opacity: 1;
}

.btn-restore-contact {
  background: transparent;
  border: none;
  color: #28a745;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-restore-contact:hover {
  background-color: #28a745;
  color: white;
  transform: scale(1.1);
}

.btn-restore-contact:active {
  transform: scale(0.95);
}

.btn-restore-contact i {
  pointer-events: none;
}

.summary-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.restore-deleted-btn {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.restore-deleted-btn:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.deleted-count {
  color: #dc3545;
  font-weight: 600;
  margin-left: 8px;
}

/* CC Section Styles */
.cc-section {
  margin-top: 24px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
}

.cc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #007bff;
}

.cc-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cc-header h4 i {
  color: #007bff;
}

.cc-count {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  background: #e9ecef;
  padding: 4px 12px;
  border-radius: 12px;
}

.cc-input-group {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.cc-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.cc-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.cc-input::placeholder {
  color: #adb5bd;
}

.cc-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cc-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

.cc-item:hover {
  border-color: #007bff;
  background: #f0f8ff;
}

.cc-email {
  font-size: 13px;
  color: #495057;
  font-weight: 500;
}

.btn-remove-cc {
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.btn-remove-cc:hover {
  background-color: #dc3545;
  color: white;
}

.cc-empty {
  text-align: center;
  padding: 16px;
  color: #6c757d;
  font-size: 14px;
  background: white;
  border: 1px dashed #dee2e6;
  border-radius: 6px;
}

.site-cell {
  text-align: center;
}

.site-count {
  display: inline-block;
  padding: 4px 8px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.step-description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 16px;
}

.message-composer {
  max-width: 100%;
  overflow-x: hidden;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Rich Text Editor Styles */
.rich-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
  overflow-x: auto;
  flex-wrap: wrap;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6c757d;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  flex-shrink: 0;
}

.toolbar-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.toolbar-btn.active {
  background: #007bff;
  color: white;
}

.toolbar-separator {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 4px;
}

.rich-text-editor {
  min-height: 120px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 0 0 8px 8px !important;
  border-top: none !important;
  line-height: 1.6;
  font-family: inherit;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.rich-text-editor:empty:before {
  content: attr(placeholder);
  color: #6c757d;
  font-style: italic;
}

.rich-text-editor:focus {
  outline: none;
  border-color: #007bff !important;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
}

.rich-text-editor.disabled-editor {
  background-color: #e9ecef;
  cursor: not-allowed;
  opacity: 0.7;
}

.rich-text-editor.disabled-editor:focus {
  border-color: #ced4da !important;
  box-shadow: none !important;
}

.message-saved-alert {
  margin-bottom: 20px;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #28a745;
  background-color: #d4edda;
  color: #155724;
}

.message-saved-alert i {
  margin-right: 8px;
}

.rich-text-editor ul,
.rich-text-editor ol {
  margin: 8px 0;
  padding-left: 20px;
}

.rich-text-editor li {
  margin: 4px 0;
}

.rich-text-editor p {
  margin: 8px 0;
}

.rich-text-editor strong {
  font-weight: 600;
}

.rich-text-editor em {
  font-style: italic;
}

.rich-text-editor u {
  text-decoration: underline;
}

.message-textarea {
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  line-height: 1.5;
}

.field-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
  text-align: right;
}

.future-feature {
  text-align: left !important;
  color: #856404 !important;
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.attachment-field {
  position: relative;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.attachment-field:hover {
  border-color: #dee2e6;
  background: #f1f3f4;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: not-allowed;
}

.attachment-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  pointer-events: none;
}

.attachment-placeholder i {
  font-size: 24px;
  color: #dee2e6;
}

.attachment-placeholder span {
  font-size: 14px;
  font-weight: 500;
}

.message-preview {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.message-preview h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

.preview-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preview-header {
  background: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-subject {
  font-weight: 600;
  color: #2c3e50;
  font-size: 16px;
}

.preview-priority {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.priority-low {
  background: #d1ecf1;
  color: #0c5460;
}

.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.priority-urgent {
  background: #f5c6cb;
  color: #721c24;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

.preview-body {
  padding: 20px;
  color: #495057;
  line-height: 1.6;
  font-size: 14px;
}

.preview-body .no-content {
  color: #6c757d;
  font-style: italic;
}

.preview-body ul,
.preview-body ol {
  margin: 8px 0;
  padding-left: 20px;
}

.preview-body li {
  margin: 4px 0;
}

.preview-body p {
  margin: 8px 0;
}

.preview-body strong {
  font-weight: 600;
}

.preview-body em {
  font-style: italic;
}

.preview-body u {
  text-decoration: underline;
}

.preview-footer {
  background: #f8f9fa;
  padding: 12px 20px;
  border-top: 1px solid #e9ecef;
  color: #6c757d;
  text-align: center;
}

.response-type-info {
  display: block;
  margin-top: 4px;
  color: #007bff;
  font-weight: 500;
}

.response-type-info i {
  margin-right: 4px;
}

/* Preview Response Buttons */
.preview-response-buttons {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 16px 20px;
}

.response-buttons-header {
  text-align: center;
  margin-bottom: 12px;
}

.response-buttons-header small {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.response-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.response-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: not-allowed;
  transition: all 0.2s ease;
  min-width: 100px;
  justify-content: center;
}

.response-btn-ok {
  background: #28a745;
  color: white;
  opacity: 0.9;
}

.response-btn-ok:hover {
  background: #218838;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.response-btn-not-ok {
  background: #d65409;
  color: white;
  opacity: 0.9;
}

.response-btn-not-ok:hover {
  background: #c54e08;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.response-btn i {
  font-size: 12px;
}

/* Preview Step Styles */
.send-summary {
  margin-bottom: 32px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-number {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.message-preview-final {
  margin-bottom: 32px;
}

.message-preview-final h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

.message-details-preview {
  margin-bottom: 32px;
}

.message-details-preview h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-label i {
  margin-right: 6px;
  color: #007bff;
}

.detail-value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.priority-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.priority-badge.priority-low {
  background: #d1ecf1;
  color: #0c5460;
}

.priority-badge.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.priority-badge.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.priority-badge.priority-urgent {
  background: #dc3545;
  color: #ffffff;
}

.recipients-preview {
  margin-bottom: 32px;
}

.recipients-preview h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

.recipients-chips-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.recipients-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.recipient-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.recipient-chip.cc-chip {
  background: #fff3cd;
  border-color: #ffc107;
}

.recipient-chip.cc-chip i {
  color: #856404;
}

.recipient-chip:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.recipient-chip.cc-chip:hover {
  background: #ffe69c;
}

.recipient-chip i {
  color: #007bff;
  font-size: 12px;
}

.recipient-chip .office-id {
  color: #6c757d;
  font-weight: 400;
  margin-left: 4px;
}

.recipients-section-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  font-size: 14px;
}

.recipients-section-label i {
  margin-right: 6px;
  color: #007bff;
}

.no-recipients {
  text-align: center;
  padding: 24px;
  color: #6c757d;
  font-style: italic;
}

.no-recipients i {
  margin-right: 8px;
  font-size: 18px;
}

.recipients-summary {
  padding-top: 12px;
  border-top: 1px solid #dee2e6;
  text-align: center;
}

.recipients-summary small {
  color: #6c757d;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.recipients-summary i {
  color: #17a2b8;
}

.send-options {
  margin-bottom: 24px;
}

.send-options h4 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 16px;
}

.option-cards {
  display: grid;
  gap: 16px;
}

.option-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-card:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.option-card.active {
  border-color: #007bff;
  background: linear-gradient(135deg, #e7f3ff 0%, #f0f8ff 100%);
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.2);
}

.option-card.active .option-icon {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.option-card.active .option-content h5 {
  color: #007bff;
  font-weight: 600;
}

.option-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8f9fa;
  border-color: #e8eaed;
  position: relative;
  pointer-events: none;
}

.option-card.disabled::after {
  content: "Future";
  position: absolute;
  top: 8px;
  right: 12px;
  background: #ffc107;
  color: #212529;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.option-card.disabled:hover {
  transform: none;
  border-color: #e8eaed;
  box-shadow: none;
  cursor: not-allowed;
}

.option-card.disabled .option-icon {
  background: #6c757d;
}

.option-card.disabled .option-content h5 {
  color: #495057;
}

.option-card.disabled .option-content p {
  color: #6c757d;
}

.option-icon {
  width: 48px;
  height: 48px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.option-content {
  flex: 1;
}

.option-content h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
}

.option-content p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
}

/* Existing styles from the wizard content */
.selection-summary {
  text-align: center;
  margin-bottom: 32px;
}

.selection-icon {
  margin-bottom: 16px;
}

.selection-icon i {
  font-size: 48px;
  color: #28a745;
}

.selection-text {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 24px;
}

.polygon-info h4 {
  color: #2c3e50;
  margin-bottom: 16px;
}

.polygon-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.stat-item {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 3px solid #007bff;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.sites-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
  margin-bottom: 20px;
}

/* Table styles for site selection */
.sites-table-container {
  margin-top: 12px;
}

.table-controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  /* margin-bottom: 16px; */
  padding: 0 4px;
}

.toggle-group-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  width: auto;
  flex-shrink: 0;
}

.toggle-group-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.toggle-group-btn i {
  font-size: 12px;
}

.sites-table-wrapper {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 8px 0;
}

.sites-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.sites-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.sites-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #e0e0e0;
}

.sites-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.sites-table tbody tr:hover:not(.region-header-row) {
  background-color: #f8f9fa;
}

.sites-table tbody tr.selected {
  background-color: #e3f2fd;
}

.sites-table tbody tr.selected:hover {
  background-color: #bbdefb;
}

.sites-table td {
  padding: 8px 12px;
  vertical-align: middle;
  background-color: inherit;
}

.checkbox-column,
.checkbox-cell {
  width: 1%;
  min-width: 30px;
  text-align: center;
}

.checkbox-cell input[type="checkbox"] {
  cursor: pointer;
}

.name-column {
  width: 45%;
  min-width: 250px;
}

.name-cell {
  font-weight: 500;
  color: #2c3e50;
}

.color-column {
  width: 20%;
  min-width: 120px;
}

.color-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
  flex-shrink: 0;
}

.color-text {
  font-size: 12px;
  color: #666;
  font-family: monospace;
}

.office-leader-column {
  width: 25%;
  min-width: 150px;
}

.office-leader-cell {
  color: #495057;
  font-weight: 500;
}

.office-address-column {
  width: 40%;
  min-width: 200px;
}

.office-address-cell {
  color: #495057;
  font-size: 13px;
  max-width: 250px;
}

.office-address {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.risk-column {
  width: 15%;
  min-width: 80px;
  text-align: center;
}

.risk-cell {
  text-align: center;
}

.site-risk {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 24px;
}

.region-header-row {
  background-color: #f1f3f4 !important;
  border-top: 2px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

.region-header-row:hover {
  background-color: #e9ecef !important;
}

.region-header-row td {
  background-color: inherit !important;
  border-bottom: none;
}

.region-checkbox-cell {
  width: 40px;
  text-align: center;
  vertical-align: middle;
}

.region-checkbox-cell input[type="checkbox"] {
  cursor: pointer;
}

.region-header-cell {
  padding: 12px 16px;
  vertical-align: middle;
}

.region-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495057;
}

.region-header i {
  color: #6c757d;
  font-size: 14px;
}

.region-name {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 13px;
}

.region-count {
  color: #6c757d;
  font-weight: normal;
  font-size: 12px;
}

.site-row {
  cursor: pointer;
  position: relative;
}

/* Remove problematic pseudo-elements and add better visual hierarchy */
.site-row td {
  border-left: 3px solid transparent;
}

.site-row.selected td {
  border-left-color: #007bff;
}

.site-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f8f9fa;
}

.site-row.disabled td {
  color: #6c757d;
}

.site-row.disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.site-row.disabled:hover {
  background-color: #f8f9fa;
}

/* Add indentation for site rows under regions */
.site-row:not(.region-header-row) td:first-child {
  padding-left: 24px;
}

/* Remove indentation for simple rows (non-grouped view) */
.site-row.simple-row td:first-child {
  padding-left: 16px;
}

.table-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-top: 12px;
  border-top: 1px solid #e0e0e0;
  font-size: 14px;
  color: #6c757d;
}

.selection-count {
  font-weight: 500;
}

.no-sites-message {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.no-sites-message i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #dee2e6;
}

.no-sites-message p {
  margin: 0;
  font-size: 16px;
}

/* Pagination Styles */
.pagination-container {
  margin-top: 20px;
  padding: 16px 0;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: #6c757d;
}

.items-info {
  font-size: 12px;
  color: #9aa0a6;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
}

.page-number-btn {
  min-width: 32px;
  height: 32px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.pagination-ellipsis {
  color: #6c757d;
  font-size: 14px;
  margin: 0 4px;
}

/* Responsive pagination */
@media (max-width: 768px) {
  .pagination-info {
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .pagination-controls {
    flex-wrap: wrap;
    gap: 4px;
  }

  .page-numbers {
    margin: 0 4px;
  }

  .pagination-btn {
    font-size: 12px;
    padding: 4px 8px;
  }
}

.site-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
}

.site-card:hover {
  background: #e9ecef;
  transform: translateY(-1px);
}

.site-card.selected {
  border-color: #007bff;
  background: #e3f2fd;
}

.site-checkbox input[type="checkbox"] {
  margin: 0;
}

.site-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.site-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.site-region {
  color: #6c757d;
}

.site-risk {
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 11px;
}

.risk-low {
  background: #d4edda;
  color: #155724;
}

.risk-medium {
  background: #fff3cd;
  color: #856404;
}

.risk-high {
  background: #f8d7da;
  color: #721c24;
}

.selection-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.action-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.action-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-card.selected {
  border-color: #007bff;
  background: #e3f2fd;
}

.action-icon {
  margin-bottom: 16px;
}

.action-icon i {
  font-size: 32px;
  color: #007bff;
}

.action-card h4 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.action-card p {
  color: #6c757d;
  margin: 0;
  font-size: 14px;
}

.confirmation-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.confirmation-item {
  margin-bottom: 12px;
  font-size: 16px;
}

.message-options {
  margin-top: 24px;
}

.message-options h4 {
  color: #2c3e50;
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
  transform: translateY(-1px);
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* Responsive design */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }

  .step-nav {
    flex-direction: column;
    gap: 24px;
    padding: 24px 20px;
    margin-bottom: 40px;
  }

  .step-nav::before,
  .step-nav::after {
    display: none;
  }

  .step-nav-item {
    flex-direction: row;
    gap: 16px;
    padding: 20px 24px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 20px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    border: 2px solid transparent;
    width: 100%;
    max-width: 320px;
    align-self: center;
    min-width: auto;
  }

  .step-nav-item.active {
    background: linear-gradient(
      135deg,
      rgba(66, 133, 244, 0.08) 0%,
      rgba(26, 115, 232, 0.08) 100%
    );
    border-color: #4285f4;
    box-shadow: 0 8px 24px rgba(66, 133, 244, 0.15);
  }

  .step-nav-item.completed {
    background: linear-gradient(
      135deg,
      rgba(52, 168, 83, 0.08) 0%,
      rgba(19, 115, 51, 0.08) 100%
    );
    border-color: #34a853;
    box-shadow: 0 6px 20px rgba(52, 168, 83, 0.15);
  }

  .step-nav-item:hover:not(.disabled) {
    transform: translateY(-2px);
    background: rgba(66, 133, 244, 0.06);
  }

  .step-circle {
    width: 56px;
    height: 56px;
    margin-bottom: 0;
    flex-shrink: 0;
    font-size: 16px;
  }

  .step-nav-item.active .step-circle {
    transform: scale(1.05);
  }

  .step-title {
    text-align: left;
    margin-top: 0;
    font-size: 15px;
    align-self: center;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .compose-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .template-section {
    align-self: flex-start;
  }

  .template-dropdown {
    right: auto;
    left: 0;
    min-width: 280px;
    max-width: 100%;
  }

  .rich-editor-toolbar {
    padding: 6px 8px;
    gap: 2px;
    overflow-x: auto;
  }

  .toolbar-btn {
    width: 28px;
    height: 28px;
    font-size: 12px;
    flex-shrink: 0;
  }

  .rich-text-editor {
    min-height: 100px;
    font-size: 16px; /* Prevent zoom on iOS */
  }

  .sites-grid {
    grid-template-columns: 1fr;
  }

  .sites-table-wrapper {
    overflow-x: auto;
  }

  .sites-table {
    min-width: 600px;
  }

  .sites-table th,
  .sites-table td {
    padding: 8px 12px;
    font-size: 13px;
  }

  .region-header-cell {
    padding: 10px 12px;
  }

  .region-name {
    font-size: 12px;
  }

  .region-count {
    font-size: 11px;
  }

  .site-row:not(.region-header-row) td:first-child {
    padding-left: 20px;
  }

  .site-row.simple-row td:first-child {
    padding-left: 12px;
  }

  .color-text {
    display: none;
  }

  .name-column {
    min-width: 150px;
  }

  .color-column {
    min-width: 60px;
  }

  .office-leader-column {
    min-width: 120px;
  }

  .office-address-column {
    min-width: 150px;
  }

  .risk-column {
    min-width: 60px;
  }

  .action-selection {
    grid-template-columns: 1fr;
  }

  .polygon-stats {
    grid-template-columns: 1fr;
  }

  .step-controls {
    flex-direction: column-reverse;
    gap: 16px;
    padding: 20px 16px;
    margin-top: 16px;
  }

  .step-content-container {
    padding: 20px 16px;
    margin-bottom: 20px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .toggle-group-btn {
    width: auto;
    justify-content: flex-start;
  }

  .selection-summary-compact {
    flex-direction: column;
    gap: 12px;
  }

  .message-composer {
    max-width: 100%;
  }

  .preview-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .preview-subject {
    font-size: 14px;
  }

  .summary-stats {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .stat-number {
    font-size: 20px;
  }

  .group-sites,
  .simple-recipients {
    gap: 6px;
  }

  .recipients-chips-container {
    padding: 16px;
  }

  .recipients-chips {
    gap: 8px;
  }

  .recipient-chip {
    padding: 6px 12px;
    font-size: 12px;
  }

  .option-card {
    padding: 16px;
  }

  .option-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .response-buttons {
    flex-direction: column;
    gap: 8px;
  }

  .response-btn {
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
    padding: 12px 16px;
  }
}
</style>
